import { isMockSurface, mockClosures } from "../mock/closures";
import { mockDispatchGroups } from "../mock/dispatch";
import { mockStores } from "../mock/stores";
import type { StoresRegister } from "../domain/stores";
import { applyOps, type PlatformStores, type StoresApplied, type StoresImported, type StoresOp } from "../domain/storesOps";
import { mockDirectory, mockRules } from "../mock/contacts";
import { MockCalls } from "../mock/calls";
import { MockSafety } from "../mock/safety";
import { MockRegisters } from "../mock/registerRows";
import type {
  AssessedHazard,
  AssessHazardInput,
  CloseHazardInput,
  FiledReport,
  FileReportInput,
  HazardActionInput,
  HazardDetail,
  HazardPage,
  IncidentCategory,
  IncidentStatus,
  RaisedIncident,
  RaiseHazardInput,
  RaiseIncidentInput,
  ReportStatus,
  SafetyReport,
  SafetyReportPage,
  SafetyVocabulary,
  SecurityIncidentPage,
} from "../domain/safety";
import { MOCK_MODELS, MOCK_REFERENCES, mockFrictionSurveys } from "../mock/friction";
import type {
  Call,
  CallDirectory,
  CallMedia,
  CallMessage,
  CallPoll,
  CallPolicy,
  CallSettingsPatch,
  CallStartOptions,
  DndState,
  IceConfig,
  LiveCall,
  Meeting,
  MeetingDraft,
  ParticipantRole,
  PolicyView,
} from "../domain/calls";
import { biasOf, mockHistory, mockInbox, mockMovements, mockSchedule, mockWave, turnOf } from "../mock/aodb";
import { mockFlights, mockStands } from "../mock/flights";
import { DOMESTIC_NODES, mockCheckpoints, mockObservations, mockPlans } from "../mock/flow";
import type { FeedDraft, FeedMessage, FeedOutcome, FeedResult, FeedTestResult, FlightFeed } from "../domain/flightFeeds";
import { mockFeedMessages, mockFeeds } from "../mock/flightFeeds";
import { flightsFrom, forecast, type Checkpoint, type FlowDay, type LanePlan, type Observation } from "../domain/flow";
import { ACCOUNT_RULES, readSap } from "../domain/sap";
import {
  readJournalCsv,
  type FinanceConnection,
  type FinanceConnectionInput,
  type FinanceImportResult,
  type FinanceLatest,
  type FinanceRun,
  type FinanceStatus,
  type FinanceTest,
} from "../domain/sapConnection";
import { assignmentsFromStands, mockLearned, mockPolicies, mockResources, mockRules as mockAllocRules } from "../mock/allocation";
import {
  applyMoves,
  assess,
  bestWindows,
  checkPlacement,
  decide,
  demandsFromMovements,
  sizeOfClass,
  learnFrom,
  solve,
  withChange,
  type AllocDay,
  type Assignment,
  type EngineInput,
  type EngineResult,
  type Learned,
  type LogEntry as AllocLogEntry,
  type Policy,
  type Proposal,
  KINDS as ALLOC_KINDS,
  type Refusal,
  type Resource,
  type ResourceKind,
  type Rule as AllocRule,
  type Trust,
  type WhatIf,
  type Window,
} from "../domain/allocation";
import { blocksFromWorkOrders } from "../features/flights/standBlocks";
import {
  checkActuals,
  checkDelayCodes,
  checkLoads,
  checkNew,
  checkTobt,
  EntryRefused,
  type ActualKey,
  type EntryResult,
  type NewMovement,
} from "../domain/flightEntries";
import type { Flight } from "../domain/flights";
import { SOURCE_LABEL as DELAY_SOURCE_LABEL, type DelaySource } from "../domain/flightDelays";
import {
  applyMvt,
  runDay,
  summarise as summariseDay,
  type DaySummary,
  type Movement,
  type MovementEvent,
  type MovementMessage,
  type Past,
  type ScheduleLine,
} from "../domain/aodb";
import {
  checkPoint,
  EVENTS,
  normalisePoints,
  reachOf,
  ruleMatches,
  type Channel,
  type ContactOrg,
  type ContactPerson,
  type Directory,
  type NoticeEvent,
  type NotifyRule,
  type Reach,
  type Recipient,
  type WorkOrderMessage,
} from "../domain/contacts";
import {
  summarise,
  type DispatchGroup,
  type DispatchInput,
  type DispatchNotification,
  type DispatchResult,
} from "../domain/dispatch";
import { mockTestRecords, mockTestTypes, mockTestingSummary, recordFromInput, setMockThresholds } from "../mock/testing";
import {
  kpiTargetRefusal,
  NO_SITE_TARGET,
  retireRefusal,
  thresholdsRefusal,
  type AmConditionInput,
  type AmConditionResult,
  type EnvelopeInput,
  type FundingEnvelope,
  type KpiTargetChange,
  type KpiTargetInput,
  type TargetStatus,
  type ThresholdsInput,
} from "../domain/entryForms";
import type { Closure, DeclareClosureInput, DeclaredClosure, EndedClosure, ListClosuresOptions } from "../domain/closures";
import {
  canTransition,
  isOpen,
  legalTransitions,
  type CreateWorkOrderInput,
  type SetSlaInput,
  type ClassifyInput,
  type ReceiptInput,
  type WorkSettings,
  type ContractorAction,
  type ReceiptVia,
  type TransitionInput,
  type WorkOrder,
  type WorkOrderEvent,
  type WorkOrderEvidence,
} from "../domain/workOrders";
import type { EstateSummary, NewNoteInput, SiteNote } from "../domain/estate";
import type { FleetAssetRow } from "../domain/fleet";
import type {
  AssetHistoryPoint,
  ConsumptionPoint,
  LatestTest,
  LogEntry,
  TestHistory,
  TestingSummary,
  NewTestInput,
  TestRecord,
  TestRecordDetail,
  TestType,
} from "../domain/testing";
import type { Alert, AlertExplanation, AlertKind } from "../domain/alerts";
import type { Inspection, InspectionDetail } from "../domain/inspections";
import {
  roundReference,
  type InspectionRound,
  type InspectionRoundInput,
  type InspectionRoundQuery,
  type InspectionRoundResult,
  type RoundAnswer,
  type RoundWork,
} from "../domain/inspectionRounds";
import type { WorkOrderPriority } from "../domain/workOrders";
import { SAMPLE_CHECKLISTS, seedRounds } from "../mock/inspectionRounds";
import type { MapFeature, MapLayer } from "../domain/geo";
import type { ComplianceReport, Rule, VerifyRuleInput, VerifyRuleResult } from "../domain/compliance";
import type { PlanAnswer } from "../domain/planning";
import type { AssistQuestion, AssistReply } from "../domain/assist";
import { mockWhenToWork } from "../mock/planning";
import { mockLiabilityCurve, mockLiabilityRegister } from "../mock/liability";
import { MockAssetManagement } from "../mock/assetManagement";
import { MockHr } from "../mock/hr";
import type {
  AmAssetDetail,
  AmCondition,
  AmFilter,
  AmImportResult,
  AmImportRow,
  AmLifecycle,
  AmMaintenance,
  AmPatch,
  AmPlan,
  AmPlanInput,
  AmPolicy,
  AmRegister,
  AmTypeDef,
  AmCreateInput,
  AmHistoryEvent,
  AmNote,
  NoteCategory,
} from "../domain/assetManagement";
import type {
  HrCost,
  HrEmployeeInput,
  HrEmployeePatch,
  HrLabour,
  HrPeople,
  HrRulesPatch,
  HrRulesView,
  HrSaudization,
} from "../domain/hr";
import type { HrAccessView } from "../domain/hrAccess";
import type {
  HrAttendanceDay,
  HrAttendanceInput,
  HrAttendanceRow,
  HrRecord,
  HrRecordInput,
  HrStaffFile,
  RecordStatus,
} from "../domain/hrFile";
import type { HrClockEvent, HrClockInput, HrGeofence, HrMe, HrSelfRequest } from "../domain/hrClock";
import type { ImportResponse } from "../domain/hrImport";
import { mockClosureCost, mockCriticalPositions } from "../mock/capacity";
import { mockDriftChecks, mockDriftSchedule } from "../mock/drift";
import { mockPopulations, mockFits } from "../mock/reliability";
import { mockLedgerFacts, mockDivergence } from "../mock/ledger";
import { mockCanWork } from "../mock/weather";
import { mockProgramme } from "../mock/programme";
import { mockStudy } from "../mock/counterfactual";
import { mockChain } from "../mock/provenance";
import type { Chain } from "../domain/provenance";
import type { Study } from "../domain/counterfactual";
import type { Programme } from "../domain/programme";
import { UTILITIES, type MeterRegister, type MeterRow, type MeterUploadResult, type NewMeterInput } from "../domain/meters";
import type { CanWork } from "../domain/weather";
import type { Fit, Population } from "../domain/reliability";
import type { Divergence, FactSummary } from "../domain/ledger";
import type { Difference, MigrationStatus, ScreenDetail, ScreenMode, Triage } from "../domain/migration";
import type { FieldPosition, ModeChange } from "./dataSource";
import type {
  AddedPerson,
  AuthStatus,
  Invitation,
  Invited,
  NewPerson,
  Person,
  ProviderChange,
  RoleRow,
  SetupState,
} from "../domain/administration";
import type { AccessLevel } from "../assistant/access";
import {
  mockMigrationDifferences,
  mockMigrationScreen,
  mockMigrationStatus,
  mockSetMode,
  mockTriage,
} from "../mock/migration";
import type { DriftCheck, DriftSchedule } from "../domain/drift";
import type { ClosureCost, CriticalReport } from "../domain/capacity";
import type { LiabilityCurve, LiabilityRegister } from "../domain/liability";
import type { Kpi } from "../domain/kpis";
import {
  DataSourceError,
  type Bootstrap,
  type DataSource,
  type DocumentQuery,
  type InspectionQuery,
  type Notification,
  type PlanQuery,
  type ClosureQuery,
  type DivergenceQuery,
  type WorkOrderPage,
  type WorkOrderQuery,
  type EventPage,
  type SkyModel,
  type ComposedText,
  type SkyConverseInput,
  type SkyConverseReply,
} from "./dataSource";
import { ALERTS, SITES, seedAlerts, seedHistory, seedKpis, seedWorkOrders } from "../mock/data";
import { INSPECTIONS } from "../mock/inspections";
import { featuresOf, GEO_LAYERS } from "../mock/geo";
import { COMPLIANCE, DOCUMENT_TYPES, DOCUMENTS, documentRows, RULES } from "../mock/compliance";
import {
  mockAttachFile,
  mockDownload,
  mockIssueCertificate,
  mockRecordCertificate,
  mockRenewCertificate,
} from "../mock/certificates";
import type { AttachedFile, CertificateIssueInput, CertificateWrite, NewCertificateInput } from "../domain/certificates";
import {
  mockApprovalsBoard,
  mockOrderApprovalRequest,
  mockRaiseApprovalRequest,
  mockSignApprovalStep,
} from "../mock/approvals";
import {
  APPROVAL_STEPS,
  type ApprovalStepAck,
  type ApprovalsBoard,
  type NewApprovalRequest,
} from "../domain/approvals";
import {
  attentionReport,
  evidenceOf,
  holdingBecause,
  holdPointReport,
  PROJECT_VOCABULARY,
  PROJECTS,
  projectRows,
  qualityOf,
  refresh,
  SHEETS,
} from "../mock/projects";
import {
  ASSERTS_ACCEPTABLE,
  BLOCKS_RELEASE,
  GATE_DECIDED,
  LIVE_STATUSES,
  type AttentionReport,
  type GateDecisionInput,
  type GateDecisionResult,
  type GateSubmitInput,
  type GateSubmitResult,
  type HoldPointReport,
  type InspectionWriteResult,
  type ItemResultInput,
  type CloseActionInput,
  type CreateProjectInput,
  type NewLogEntryInput,
  type ProjectLog,
  type ProjectLogEntry,
  type ScheduleInput,
  type ScheduleResult,
  type UpdateProjectInput,
  type ProjectDetail,
  type ProjectInspectionDetail,
  type ProjectQuery,
  type ProjectRow,
  type ProjectVocabulary,
  type RecordResultInput,
} from "../domain/projects";
import {
  REVISION_TRANSITIONS,
  type DocumentDetail,
  type DocumentRow,
  type DocumentType,
  type NewDocumentInput,
  type NewDocumentResult,
  type NewRevisionInput,
  type NewRevisionResult,
  type Revision,
  type RevisionTransitionInput,
  type RevisionTransitionResult,
} from "../domain/library";

/**
 * In-memory adapter. Behaves like the platform where it matters for the UI:
 * it enforces the lifecycle, refuses illegal transitions naming the allowed
 * states (as `workorders.transition` does), appends an event per transition,
 * reports legal transitions on a work order, and links alerts to the work
 * they raised. State lives for the page session only.
 */
/* a 1×1 PNG, enough for an <img> to render and a viewer to see the file exists */
const PHOTO_PNG = "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
function samplePhoto(): Blob {
  const bin = atob(PHOTO_PNG);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: "image/png" });
}

/* The roles the sample shows: the built-ins it would meet on a platform, plus
   one of the tenant's own so the level control has something it may edit. */
const SAMPLE_ROLES: RoleRow[] = [
  {
    key: "administrator",
    name: "Administrator",
    permissions: ["*"],
    accessLevel: "master",
    builtin: true,
    description: "Everything, including granting access.",
  },
  {
    key: "accountable_manager",
    name: "Accountable Manager",
    permissions: ["site:read", "insight:read", "document:approve"],
    accessLevel: "executive",
    builtin: true,
    description: "The person the regulator holds responsible.",
  },
  {
    key: "supervisor",
    name: "Supervisor",
    permissions: ["site:read", "work_order:write", "work_order:verify"],
    accessLevel: "engineering",
    builtin: true,
    description: "Runs the work and verifies it.",
  },
  {
    key: "technician",
    name: "Technician",
    permissions: ["site:read", "work_order:write"],
    accessLevel: "operations",
    builtin: true,
    description: "Does the work and records it.",
  },
  {
    key: "night_shift",
    name: "Night Shift",
    permissions: ["site:read", "work_order:write"],
    accessLevel: "operations",
    builtin: false,
    description: "This tenant's own role — its level may be set here.",
  },
];

/* build 4: the stores chain and the six approval steps, as the platform's administrator holds them (0090) */
const MOCK_APPROVER_PERMISSIONS = [
  "stores:read",
  "stores:write",
  "stores:approve",
  ...APPROVAL_STEPS.map((s) => s.permission),
];

/* ── build 6: who set each figure, as record.change keeps it on the row ── */
const PLATFORM_FIELD: Record<string, string> = {
  actualAt: "actual_at",
  estimatedAt: "estimated_at",
  scheduledAt: "scheduled_at",
  status: "status",
  registration: "registration",
  classKey: "class_key",
  counterpartNode: "counterpart_node",
  serviceType: "service_type",
  remarks: "remarks",
  cancelledReason: "cancelled_reason",
  delayCodes: "delay_codes",
  capacity: "capacity",
};
const KEYED_FIELDS = ["milestones", "loads", "handling"] as const;

function provenanceMarks(
  before: Movement,
  changes: Partial<Movement>,
  changed: (keyof Movement)[],
  source: string,
  by?: string,
): Record<string, { source: string; by?: string; at: string }> {
  const mark = { source, at: new Date().toISOString(), ...(by ? { by } : {}) };
  const out: Record<string, { source: string; by?: string; at: string }> = {};
  for (const k of changed) {
    if ((KEYED_FIELDS as readonly string[]).includes(k)) {
      const old = (before[k] ?? {}) as Record<string, unknown>;
      const now = (changes[k] ?? {}) as Record<string, unknown>;
      for (const sub of new Set([...Object.keys(old), ...Object.keys(now)]))
        if (JSON.stringify(old[sub] ?? null) !== JSON.stringify(now[sub] ?? null)) out[`${k}.${sub}`] = mark;
    } else if (PLATFORM_FIELD[k]) out[PLATFORM_FIELD[k]] = mark;
  }
  return out;
}

/* ── build 6: the register's changeable fields, as store.change_resource names them ── */
const REGISTER_FIELDS = ["name", "group", "seq", "maxSize", "servedInPlace", "uses", "excludes", "links", "bus", "active"] as const;
const REGISTER_USES = ["pax", "cargo", "ferry", "positioning", "ga", "military", "maintenance", "other"] as const;
const KIND_ONE: Record<ResourceKind, string> = { position: "stand", gate: "gate", counter: "counter", belt: "belt" };

export class MockSource implements DataSource {
  readonly kind = "mock" as const;
  private orders: WorkOrder[];
  private projectLogs = new Map<string, ProjectLogEntry[]>();
  private history = new Map<string, WorkOrderEvent[]>();
  private readonly latency: number;
  private readonly principal = "Operator";
  /* staff calls on the sample: simulated colleagues, said so on every call */
  private readonly staffCalls = new MockCalls();
  /* the sample's own small directory, worked by the administration screen */
  private people: Person[] = [
    {
      id: "sample-1",
      kind: "user",
      externalId: "adil.zaki",
      displayName: "Adil Zaki",
      email: "adil.zaki@example",
      groups: ["administrators"],
      roles: ["administrator"],
      lastSeenAt: new Date().toISOString(),
    },
    {
      id: "sample-2",
      kind: "user",
      externalId: "sup.mona",
      displayName: "Mona Al-Harbi",
      email: "mona@example",
      groups: ["maintenance_supervisors"],
      roles: ["supervisor"],
    },
    {
      id: "sample-3",
      kind: "user",
      externalId: "mgr.khalid",
      displayName: "Khalid Al-Zahrani",
      email: "khalid@example",
      groups: ["accountable"],
      roles: ["accountable_manager"],
    },
    {
      id: "sample-4",
      kind: "user",
      externalId: "tech.rashid",
      displayName: "Rashid Al-Dosari",
      email: "rashid@example",
      groups: ["agl"],
      roles: ["technician"],
    },
    /* one with no role, because that is the state the checklist is for */
    {
      id: "sample-5",
      kind: "user",
      externalId: "new.hire",
      displayName: "Sara Al-Amri",
      email: "sara@example",
      groups: [],
      roles: [],
    },
  ];

  constructor(opts: { latencyMs?: number } = {}) {
    this.latency = opts.latencyMs ?? 120;
    this.orders = seedWorkOrders();
    for (const wo of this.orders) this.history.set(wo.id, seedHistory(wo));
    this.seedDelivery();
  }

  private async delay<T>(v: T): Promise<T> {
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency));
    return structuredClone(v);
  }

  bootstrap(): Promise<Bootstrap> {
    return this.delay({
      tenant: { name: "Sample estate", slug: "tibah" },
      principal: {
        name: this.principal,
        subject: "demo",
        method: "sample",
        groups: ["maintenance_supervisors"],
        /* The sample is a demonstration of the whole platform, so it shows
           everything. It says "sample" as its method, and the Settings panel
           and the security panel both name that as the reason — a
           demonstration that quietly granted itself master and did not say so
           would be the same failure as a gate left open and unlabelled. */
        accessLevel: "master",
        permissions: [
          "site:read",
          "site:write",
          "work_order:read",
          "work_order:write",
          "work_order:verify",
          "insight:read",
          "inspection:read",
          /* build 7: the boards file their rounds and daily utility checks as inspections, and
             the sample shows the whole platform — without it every round said "not permitted" */
          "inspection:write",
          "geo:read",
          "document:read",
          "document:write",
          "document:approve",
          "rule:verify",
          "project:read",
          "project:write",
          /* Both dangerous keys, so the sample can show the two acts the pack
             separates. A real principal rarely holds both. */
          "project:gate",
          "project:release_hold",
          /* The sample now holds a testing register, so it holds the keys to
             read and add to it. A register somebody can search but not record
             against demonstrates half a screen. */
          "test:read",
          "test:write",
          /* The safety register and the security incident register: the
             sample files, reads and works them, and holds the two dangerous
             safety keys so it can show closing and accepting a risk — a
             real principal rarely holds those. The reporter-identity key is
             left out: the sample has no screen that reveals a reporter. */
          "safety:read",
          "safety:write",
          "safety:report",
          "safety:report_read",
          "safety:close",
          "security:read",
          "security:incident_write",
          "security:read_restricted",
          /* Loading a friction survey, and saving the rubber model's figures
             for the airport — what a supervisor holds on the platform (0088). */
          "reading:write",
          "insight:run",
          /* The planning join needs BOTH, and the platform enforces that: the
             joint verdict is only as trustworthy as its weaker half. */
          "capacity:read",
          "weather:read",
          /* Asset Management: registering and editing assets, type policies
             and maintenance plans need asset:write; notes on an asset need
             note:write. Without them the sample showed a register nobody
             could add to. */
          "asset:write",
          "note:write",
          /* HR: the workforce and its pay are read with hr:read and changed
             with hr:write — neither comes with site:read on the platform. */
          "hr:read",
          "hr:write",
          /* the sample signs in as the HR super admin: every level is shown
             by previewing down from it on the Access levels screen */
          "hr:pay",
          "hr:confidential",
          "hr:approve",
          "hr:admin",
          /* The finance system: its figures, a file sent to it, and the SAP
             connection panel — which in the sample keeps its settings for the
             session and says it makes no call. */
          "finance:read",
          "finance:import",
          "finance:connect",
          /* Staff calls: calling, and the administrator's rule for who may
             call whom. On the sample nobody is on the other end, and the
             call window says so. */
          "calls:use",
          "calls:policy",
          /* The stores chain and the six approval steps (build 4): what the
             platform's administrator holds. The sample's one person may still
             sign only one step of a request, as anybody may. */
          ...MOCK_APPROVER_PERMISSIONS,
          /* build 6B: moving a KPI target, and setting funding envelopes — an
             accountable manager's or an administrator's on the platform. The
             sample holds them so its target and envelope forms can be used. */
          "kpi:target",
          "insight:write",
        ],
      },
      sites: SITES,
      packs: [{ key: "airports", version: "1.0.0" }],
      developmentAuth: false,
      receivedAt: new Date().toISOString(),
    });
  }

  listWorkOrders(q: WorkOrderQuery): Promise<WorkOrderPage> {
    const items = this.orders
      .filter((w) => w.siteId === q.siteId)
      .filter((w) => !q.status || w.status === q.status)
      .sort((a, b) => a.priority - b.priority || b.openedAt.localeCompare(a.openedAt))
      .map((w) => this.withTransitions(w));
    return this.delay({ items, truncated: false });
  }

  async getWorkOrder(id: string): Promise<WorkOrder> {
    const wo = this.find(id);
    return this.delay(this.withTransitions(wo));
  }

  async getWorkOrderHistory(id: string): Promise<WorkOrderEvent[]> {
    this.find(id);
    return this.delay(this.history.get(id) ?? []);
  }

  createWorkOrder(input: CreateWorkOrderInput): Promise<WorkOrder> {
    /* build 3: as the platform, the same client id files one order */
    const again = input.clientId ? this.woClientIds.get(input.clientId) : undefined;
    if (again) return this.delay(this.withTransitions(this.find(again)));
    const n = this.orders.length + 1;
    const now = new Date().toISOString();
    const wo: WorkOrder = {
      id: `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`,
      number: `WO-${String(n).padStart(6, "0")}`,
      siteId: input.siteId,
      title: input.title,
      kind: input.kind,
      priority: input.priority,
      status: "draft",
      description: input.description,
      zoneCode: input.zoneCode,
      assetTag: input.assetTag,
      openedAt: now,
      slaHours: input.slaHours,
      dueAt: input.slaHours ? new Date(Date.parse(now) + input.slaHours * 3_600_000).toISOString() : undefined,
      capex: input.capex ?? false,
      alertId: input.alertId,
      attrs: classAttrs(input),
      requestedBy: this.principal,
    };
    this.orders.unshift(wo);
    if (input.clientId) this.woClientIds.set(input.clientId, wo.id);
    this.history.set(wo.id, [
      {
        id: `${wo.id}-ev1`,
        workOrderId: wo.id,
        at: now,
        by: this.principal,
        from: null,
        to: "draft",
        note: "Raised in console",
      },
    ]);
    if (input.alertId) {
      const alert = ALERTS.find((a) => a.id === input.alertId);
      alert?.resultingActions.push({ kind: "work_order", ref: wo.number, title: wo.title, status: "draft" });
    }
    this.applyRules(wo, "created");
    return this.delay(this.withTransitions(wo));
  }

  /* The sample carries no asset register: a module screen on it says there is nothing registered. */
  /**
   * Empty, deliberately.
   *
   * The sample has no platform behind it, so it has no platform assets. The
   * equipment register reads that emptiness as "this deployment's pack does
   * not declare vehicle types" and falls back to its entered copy, which is
   * exactly what an airport on an older pack should see. Returning the sample
   * fleet here would make the mock claim a platform read it never did.
   */
  async listAssetsOfTypes(_siteId: string, _assetTypes: readonly string[]): Promise<FleetAssetRow[]> {
    return this.delay([]);
  }

  async getEstateSummary(_siteId: string, assetTypes: string[]): Promise<EstateSummary> {
    return this.delay({
      assetTypes: [],
      unknownAssetTypes: [...assetTypes],
      assetsTotal: 0,
      defects: {
        open: 0,
        raised30d: 0,
        resolved30d: 0,
        assetsWithOpen: 0,
        openList: [],
        openListTotal: 0,
        byTypeSeverity: [],
      },
      alerts: { open: 0, list: [] },
      readings: [],
      latestReadings: [],
    });
  }

  /*
   * The sample's testing register.
   *
   * It used to declare no test types and hold no records, so Testing &
   * Records opened empty and a test could not be recorded either — a screen
   * built for storing and finding evidence that demonstrated neither. The
   * records are demonstration data and say so on every row; what is entered
   * through the form is kept apart from them and is not marked demo.
   *
   * Entered records live for as long as the page does. The sample has no
   * store behind it, which is the honest limit of a copy with no platform,
   * and the screen says as much when it cannot reach one.
   */
  private entered: TestRecord[] = [];

  async listTestTypes(): Promise<TestType[]> {
    return this.delay(mockTestTypes());
  }

  async listTestRecords(): Promise<TestRecord[]> {
    return this.delay(this.allTestRecords());
  }

  private allTestRecords(): TestRecord[] {
    return [...this.entered, ...mockTestRecords()].sort((a, b) => b.performedOn.localeCompare(a.performedOn));
  }

  async getTestingSummary(): Promise<TestingSummary> {
    return this.delay(mockTestingSummary(this.allTestRecords()));
  }

  async getTestRecord(id: string): Promise<TestRecordDetail> {
    const record = this.allTestRecords().find((r) => r.id === id);
    if (!record) throw new DataSourceError("no test record with that reference", { status: 404 });
    return this.delay({
      record: { ...record, thresholdsUsed: mockTestTypes().find((t) => t.key === record.testType)?.thresholds ?? [] },
      results: [],
      files: [],
      comparison: null,
      readingsWritten: { count: 0 },
    });
  }

  async getLatestTest(): Promise<LatestTest> {
    return this.delay({ latest: null, results: [], comparison: null, thresholdsUsed: [] });
  }

  async createTestRecord(_siteId: string, input: NewTestInput): Promise<{ id: string; reference: string }> {
    this.checkTest(input);
    const rec = recordFromInput(input, this.entered.length + 1);
    this.entered = [rec, ...this.entered];
    return this.delay({ id: rec.id, reference: rec.reference });
  }

  /* ── build 7: the assessments board files test records; refuse what the platform refuses ── */
  private checkTest(input: NewTestInput): void {
    /* the sample declares other keys than a tenant does (the friction return files `friction`), so an
       unknown key is taken as it always was; a declared judged type is held to the platform's rule */
    const ty = mockTestTypes().find((x) => x.key === input.testType);
    if (Date.parse(`${input.performedOn.slice(0, 10)}T00:00:00Z`) > Date.now() + 86_400_000)
      throw new DataSourceError("a test cannot have been performed in the future", { status: 400 });
    if (ty?.mode === "judged") {
      if (!["pass", "attention", "fail", "not_assessed"].includes(input.outcome ?? ""))
        throw new DataSourceError(
          "this test is judged by an engineer: give an outcome of pass, attention or fail (or not_assessed, saying why no verdict was given)",
          { status: 400 },
        );
      if (!input.outcomeBasis?.trim()) throw new DataSourceError("an engineer's verdict needs its basis", { status: 400 });
    }
  }

  /** A record entered in this session, corrected as its next revision; the sample's own cannot be. */
  async reviseTestRecord(
    id: string,
    input: NewTestInput & { reason: string },
  ): Promise<{ id: string; reference: string; revision: number }> {
    const prior = this.entered.find((r) => r.id === id);
    if (!prior) throw new DataSourceError("the sample holds no test records", { status: 404 });
    if (prior.supersededBy) throw new DataSourceError("this record has already been revised", { status: 409 });
    if (!input.reason?.trim()) throw new DataSourceError("a revision needs its reason", { status: 400 });
    this.checkTest({ ...input, testType: input.testType || prior.testType });
    const next: TestRecord = {
      ...recordFromInput({ ...input, testType: input.testType || prior.testType }, this.entered.length + 1),
      reference: prior.reference,
      revision: prior.revision + 1,
      supersedes: prior.id,
      amendmentReason: input.reason.trim(),
    };
    this.entered = [next, ...this.entered.map((r) => (r.id === id ? { ...r, supersededBy: next.id } : r))];
    return this.delay({ id: next.id, reference: next.reference, revision: next.revision });
  }

  async getTestHistory(): Promise<TestHistory> {
    throw new DataSourceError("the sample holds no test records", { status: 404 });
  }

  async addTestAction(): Promise<void> {
    throw new DataSourceError("the sample holds no test records", { status: 404 });
  }

  async getTestingActivity(): Promise<LogEntry[]> {
    return this.delay([]);
  }

  async getAssetTestHistory(): Promise<AssetHistoryPoint[]> {
    return this.delay([]);
  }

  async uploadTestFile(): Promise<void> {
    throw new DataSourceError("the sample does not store files", { status: 400 });
  }

  /* A photograph, for the two the sample holds against the runway rubber removal. */
  async downloadFile(objectId: string): Promise<Blob> {
    /* not through `delay`, which clones through JSON and would hand back a plain object instead of a Blob */
    if (objectId.startsWith("mock-photo-")) return Promise.resolve(samplePhoto());
    throw new DataSourceError("the sample does not store files", { status: 404 });
  }

  async listClosures(_siteId: string, since?: string, opts?: ListClosuresOptions): Promise<Closure[]> {
    const from = since ? Date.parse(since) : -Infinity;
    const rows = this.heldClosures()
      .filter((h) => opts?.includePlanned || (h.started && h.c.status !== "cancelled"))
      .filter((h) => Date.parse(h.c.endedAt ?? h.started ?? h.c.plannedStart ?? "") >= from)
      .sort((a, b) => Date.parse(b.started ?? b.c.plannedStart ?? "") - Date.parse(a.started ?? a.c.plannedStart ?? ""))
      .map((h) => ({ ...h.c, startedAt: h.started ?? h.c.plannedStart ?? h.c.declaredAt ?? "" }));
    return this.delay(rows);
  }

  /* Before-and-after photographs on the rubber removal; nothing on anything else. */
  async listEvidence(subjectKind: string, subjectId: string): Promise<WorkOrderEvidence[]> {
    if (subjectKind !== "work_order") return this.delay([]);
    const w = await this.getWorkOrder(subjectId).catch(() => null);
    if (!w || w.number !== "WO-000006") return this.delay([]);
    return this.delay([
      { id: "ev-6a", phase: "before", createdAt: w.openedAt, objectId: "mock-photo-before", contentType: "image/png" },
      { id: "ev-6b", phase: "after", createdAt: w.openedAt, objectId: "mock-photo-after", contentType: "image/png" },
    ]);
  }

  async getConsumption(): Promise<ConsumptionPoint[]> {
    return this.delay([]);
  }

  /* The sample estate's meters: named, typed, and connected to nothing — so
     the register shows what a site looks like before its first feed, and
     the Green programme's demonstration series is the honest answer. */
  private meters: MeterRow[] = (
    [
      ["MTR-INCOMER", "Terminal main incomer", "electricity_meter", "Electricity Meter", "incomer"],
      ["MTR-SUB-A", "Terminal sub-meter A", "electricity_meter", "Electricity Meter", "sub"],
      ["MTR-SUB-B", "Terminal sub-meter B", "electricity_meter", "Electricity Meter", "sub"],
      ["MTR-SUB-C", "Terminal sub-meter C", "electricity_meter", "Electricity Meter", "sub"],
      ["WTR-INCOMER", "Terminal water incomer", "water_meter", "Water Meter", "incomer"],
      ["WTR-FIRE", "Fire main sub-meter", "water_meter", "Water Meter", "sub"],
      ["CHW-PLANT-1", "Central chiller plant", "chilled_water_meter", "Chilled Water Meter", "incomer"],
    ] as const
  ).map(([assetTag, name, assetType, assetTypeName, role]) => ({
    assetId: `mock-${assetTag}`,
    assetTag,
    name,
    assetType,
    assetTypeName,
    role,
    channelId: null,
    staleAfterMin: 360,
    rollovers: 0,
    resets: 0,
    status: "never" as const,
  }));
  private meterSeen = new Set<string>();

  async listMeters(_siteId: string, assetTypes: string[]): Promise<MeterRegister> {
    const now = Date.now();
    const meters = this.meters
      .filter((m) => !assetTypes.length || assetTypes.includes(m.assetType))
      .map((m) => {
        if (!m.lastObservedAt) return m;
        const ageMin = (now - Date.parse(m.lastObservedAt)) / 60_000;
        return { ...m, ageMin, status: ageMin <= m.staleAfterMin ? ("live" as const) : ("stale" as const) };
      });
    const connected = this.meters.some((m) => m.channelId);
    return this.delay({
      meters,
      connector: connected
        ? { key: "meter_feed-sample", name: "Building meters — sample", pushPath: "/v1/ingest/meter_feed-sample" }
        : null,
      demo: true,
    });
  }

  async registerMeter(_siteId: string, input: NewMeterInput): Promise<void> {
    const u = UTILITIES.find((x) => x.key === input.utility)!;
    let row = this.meters.find((m) => m.assetTag === input.assetTag);
    if (!row) {
      if (!input.createName) throw new Error(`no asset tagged ${input.assetTag} at this site`);
      row = {
        assetId: `mock-${input.assetTag}`,
        assetTag: input.assetTag,
        name: input.createName,
        assetType: u.assetType,
        assetTypeName: u.label,
        role: input.role ?? "sub",
        channelId: null,
        staleAfterMin: 360,
        rollovers: 0,
        resets: 0,
        status: "never",
      };
      this.meters.push(row);
    }
    Object.assign(row, {
      channelId: `ch-${input.assetTag}`,
      pointKey: input.pointKey || input.assetTag,
      readingKind: input.readingKind,
      measurement: u.measurement,
      unit: u.unit,
      staleAfterMin: input.staleAfterMin ?? 360,
    });
    await this.delay(null);
  }

  async uploadMeterReadings(_siteId: string, csv: string): Promise<MeterUploadResult> {
    const lines = csv.trim().split(/\r?\n/);
    const sep = [",", ";", "\t"].reduce((a, b) => (lines[0].split(b).length > lines[0].split(a).length ? b : a));
    const head = lines[0].toLowerCase().split(sep).map((h) => h.trim());
    const col = (names: string[]) => head.findIndex((h) => names.includes(h));
    const [cm, ct, cv] = [col(["meter", "point", "tag"]), col(["timestamp", "ts", "time"]), col(["value", "reading"])];
    const out: MeterUploadResult = { received: 0, accepted: 0, duplicate: 0, rejected: 0, replayed: false, rejections: [] };
    if (cm < 0 || ct < 0 || cv < 0) throw new Error("the header must name the meter, the timestamp and the value");
    lines.slice(1).forEach((line, i) => {
      if (!line.trim()) return;
      out.received += 1;
      const cells = line.split(sep).map((c) => c.trim());
      const meter = this.meters.find((m) => m.channelId && m.pointKey === cells[cm]);
      const value = Number(cells[cv]);
      const at = Date.parse(cells[ct].replace(" ", "T"));
      const reason = !meter
        ? `no counter channel '${cells[cm]}' at this site; register the meter first`
        : cells[cv] === "" || !Number.isFinite(value)
          ? "a meter reading that is not a number is not a reading"
          : Number.isNaN(at)
            ? "the timestamp could not be read"
            : null;
      if (reason || !meter) {
        out.rejected += 1;
        out.rejections.push({ line: i + 2, reason: reason ?? "" });
        return;
      }
      const key = `${meter.pointKey}@${at}`;
      if (this.meterSeen.has(key)) {
        out.duplicate += 1;
        return;
      }
      this.meterSeen.add(key);
      out.accepted += 1;
      if (!meter.lastObservedAt || at > Date.parse(meter.lastObservedAt)) {
        meter.lastObservedAt = new Date(at).toISOString();
        meter.lastValue = value;
        meter.lastSource = "file";
      }
    });
    out.replayed = out.received > 0 && out.duplicate === out.received;
    return this.delay(out);
  }

  private notes: SiteNote[] = [];

  async listNotes(_siteId: string, topic: string): Promise<SiteNote[]> {
    return this.delay(
      this.notes
        .filter((n) => n.topic === topic)
        .slice()
        .reverse(),
    );
  }

  /* ── build 2: friction ── */

  /* What a person loads during the visit; refused, replayed and held as the platform would. */
  private frictionSurveys = mockFrictionSurveys();
  private frictionKeys = new Map<string, import("../domain/friction").FrictionSurveyAck>();
  private modelParams = new Map<string, import("../domain/friction").ModelParams>();

  async listFrictionSurveys(_siteId: string): Promise<import("../domain/friction").FrictionSurvey[]> {
    return this.delay(this.frictionSurveys.slice().sort((a, b) => b.observedAt.localeCompare(a.observedAt)));
  }

  async importFrictionSurvey(
    _siteId: string,
    input: import("../domain/friction").NewFrictionSurvey,
    idempotencyKey?: string,
  ): Promise<import("../domain/friction").FrictionSurveyAck> {
    const seen = idempotencyKey ? this.frictionKeys.get(idempotencyKey) : undefined;
    if (seen) return this.delay({ ...seen, replayed: true });
    const ref = MOCK_REFERENCES[input.referenceKey];
    if (!ref) throw new DataSourceError(`linear reference '${input.referenceKey}' not found`, { status: 400 });
    if (!input.runs.length || input.runs.some((r) => !r.segments.length))
      throw new DataSourceError("every run needs its readings", { status: 400 });
    if (this.frictionSurveys.some((s) => s.surveyId === input.surveyId))
      throw new DataSourceError(`Survey ${input.surveyId} is already on the platform.`, { status: 409 });
    const turn = input.chainage === "as_flown";
    const runs = input.runs.map((r) => {
      const segs = r.segments.map((s) =>
        turn && r.direction !== ref.forward
          ? { fromM: +(ref.lengthM - s.toM).toFixed(2), toM: +(ref.lengthM - s.fromM).toFixed(2), value: s.value }
          : s,
      );
      segs.sort((a, b) => a.fromM - b.fromM);
      return {
        direction: r.direction,
        lap: r.lap,
        offsetM: r.offsetM,
        label: r.label,
        fromM: segs.map((s) => s.fromM),
        toM: segs.map((s) => s.toM),
        value: segs.map((s) => s.value),
      };
    });
    const at = new Date().toISOString();
    this.frictionSurveys.push({
      surveyId: input.surveyId,
      kind: input.kind ?? "survey",
      observedAt: input.observedAt,
      observedOn: input.observedAt.slice(0, 10),
      reference: input.referenceKey,
      referenceLengthM: ref.lengthM,
      forward: ref.forward,
      device: input.device,
      speedKmh: input.speedKmh,
      levels: input.levels,
      header: input.header ?? {},
      importedBy: this.principal,
      importedAt: at,
      runs,
    });
    const ack = {
      surveyId: input.surveyId,
      runs: runs.length,
      segments: runs.reduce((t, r) => t + r.value.length, 0),
      documents: runs.map((_, i) => `mock-survey-${this.frictionSurveys.length}-${i + 1}`),
      importedAt: at,
      importedBy: this.principal,
    };
    if (idempotencyKey) this.frictionKeys.set(idempotencyKey, ack);
    return this.delay(ack);
  }

  async recordCompletedWork(
    input: import("../domain/friction").CompletedWorkInput,
  ): Promise<import("../domain/friction").CompletedWorkAck> {
    const day = input.completedOn.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) throw new DataSourceError("completed_on must be a date, YYYY-MM-DD", { status: 400 });
    if (Date.parse(`${day}T00:00:00Z`) > Date.now() + 86_400_000)
      throw new DataSourceError("completed_on is in the future; record work once it is done", { status: 400 });
    if (!input.clientId) throw new DataSourceError("client_id is required", { status: 400 });
    const held = this.orders.find((w) => w.clientId === input.clientId);
    if (held)
      return this.delay({ id: held.id, number: held.number, status: held.status, created: false, completedAt: held.completedAt });
    const n = this.orders.length + 1;
    const at = `${day}T12:00:00.000Z`;
    const wo: WorkOrder = {
      id: `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`,
      number: `WO-${String(n).padStart(6, "0")}`,
      siteId: input.siteId,
      title: input.title,
      kind: (input.kind ?? "preventive") as WorkOrder["kind"],
      priority: (input.priority ?? 3) as WorkOrder["priority"],
      status: "completed",
      description: input.description,
      zoneCode: input.zoneFallback ?? input.zoneCode,
      openedAt: at,
      startedAt: at,
      completedAt: at,
      clientId: input.clientId,
      attrs: { ...input.attrs, activity: input.activity, recorded_after: true },
      requestedBy: this.principal,
      completedBy: this.principal,
    };
    this.orders.unshift(wo);
    this.history.set(wo.id, [
      {
        id: `${wo.id}-ev1`,
        workOrderId: wo.id,
        at: new Date().toISOString(),
        by: this.principal,
        from: null,
        to: "completed",
        note: `Recorded as done on ${day}.`,
      },
    ]);
    return this.delay({ id: wo.id, number: wo.number, status: wo.status, created: true, completedAt: at, zoneCode: wo.zoneCode });
  }

  async amendCompletedWork(
    id: string,
    patch: import("../domain/friction").CompletedWorkPatch,
  ): Promise<import("../domain/friction").CompletedWorkAmended> {
    const wo = this.find(id);
    if (wo.status !== "completed" && wo.status !== "verified")
      throw new DataSourceError(`${wo.number} is ${wo.status}; only finished work has a record to amend`, { status: 409 });
    const held = wo.attrs ?? {};
    const changed = Object.entries(patch.attrs ?? {}).filter(([k, v]) => JSON.stringify(held[k]) !== JSON.stringify(v));
    const day = patch.completedOn?.slice(0, 10);
    const moved = !!day && wo.completedAt?.slice(0, 10) !== day;
    if (!changed.length && !moved) return this.delay({ id: wo.id, number: wo.number, status: wo.status, changed: false, fields: [] });
    wo.attrs = { ...held, ...Object.fromEntries(changed) };
    if (moved) wo.completedAt = `${day}T12:00:00.000Z`;
    const fields = [...changed.map(([k]) => k).sort(), ...(moved ? ["completed_on"] : [])];
    this.history.get(wo.id)?.push({
      id: `${wo.id}-ev${(this.history.get(wo.id)?.length ?? 0) + 1}`,
      workOrderId: wo.id,
      at: new Date().toISOString(),
      by: this.principal,
      from: wo.status,
      to: wo.status,
      note: `Record amended: ${fields.join(", ").replace(/_/g, " ")}`,
    });
    return this.delay({ id: wo.id, number: wo.number, status: wo.status, changed: true, fields });
  }

  async getModelParams(_siteId: string, model: string): Promise<import("../domain/friction").ModelParams> {
    return this.delay(this.modelParams.get(model) ?? { model, params: {}, inputs: null, zones: {} });
  }

  async saveModelParams(
    _siteId: string,
    model: string,
    input: import("../domain/friction").ModelParamsInput,
  ): Promise<import("../domain/friction").ModelParams> {
    if (!MOCK_MODELS.has(model)) throw new DataSourceError(`there is no model '${model}'`, { status: 400 });
    for (const [k, v] of Object.entries(input.params))
      if (typeof v !== "number" || !Number.isFinite(v))
        throw new DataSourceError(`parameter '${k}' must be a number`, { status: 400 });
    const saved = {
      model,
      params: { ...input.params },
      inputs: input.inputs ?? null,
      zones: input.zones ?? {},
      note: input.note,
      savedBy: this.principal,
      savedAt: new Date().toISOString(),
    };
    this.modelParams.set(model, saved);
    /* the sample has no runway thirds mapped, as Madinah has none yet */
    return this.delay({ ...saved, zonesApplied: [], zonesMissing: Object.keys(saved.zones) });
  }

  async addNote(_siteId: string, input: NewNoteInput): Promise<void> {
    if (!input.body.trim()) throw new DataSourceError("a note needs some text", { status: 400 });
    this.notes.push({
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      createdBy: this.principal,
      ...input,
      body: input.body.trim(),
    });
    return this.delay(undefined);
  }

  /* The sample has no person to keep a setting for on a server, so it keeps it in this browser. */
  /* The sample never changes behind the screens, so there is nothing to report. */
  async pollEvents(_siteId: string, after: number): Promise<EventPage> {
    return { events: [], cursor: after, more: false };
  }

  async getPreference(key: string): Promise<unknown> {
    try {
      const raw = localStorage.getItem(`sky.pref.sample.${key}`);
      return this.delay(raw ? (JSON.parse(raw) as unknown) : null);
    } catch {
      return this.delay(null);
    }
  }

  async setPreference(key: string, value: unknown): Promise<void> {
    try {
      localStorage.setItem(`sky.pref.sample.${key}`, JSON.stringify(value));
    } catch {
      throw new DataSourceError("this browser will not store the setting", { status: 507 });
    }
    return this.delay(undefined);
  }

  async deletePreference(key: string): Promise<void> {
    try {
      localStorage.removeItem(`sky.pref.sample.${key}`);
    } catch {
      /* nothing stored, nothing to forget */
    }
    return this.delay(undefined);
  }

  /* ── dispatch: a P1 for a team, and what would have been sent to each member ── */
  private dispatchGroups: DispatchGroup[] = mockDispatchGroups();
  private dispatchOutbox = new Map<string, DispatchNotification[]>();

  async dispatchTeam(input: DispatchInput): Promise<DispatchResult> {
    let group = this.dispatchGroups.find((g) => g.key === input.groupKey);
    if (!group) {
      group = { key: input.groupKey, name: input.groupName ?? input.groupKey, kind: input.groupKind, members: [] };
      this.dispatchGroups.push(group);
    }
    const wo = await this.createWorkOrder({
      siteId: input.siteId,
      title: input.title,
      kind: "corrective",
      priority: 1,
      description: input.description,
      zoneCode: input.zoneCode,
      assetTag: input.assetTag,
      slaHours: 4,
    });
    const held = this.find(wo.id);
    held.crew = group.name;
    held.attrs = { ...held.attrs, dispatchGroup: group.key };
    await this.transitionWorkOrder(wo.id, { to: "approved" });
    await this.transitionWorkOrder(wo.id, { to: "dispatched", note: "dispatched from the map" });
    const rows: DispatchNotification[] = [];
    let n = 0;
    for (const m of group.members)
      for (const [channel, to] of [
        ["sms", m.phone],
        ["email", m.email],
        ["app", m.principalId],
      ] as const) {
        rows.push({
          id: `${wo.id}-n${++n}`,
          recipient: m.name,
          channel,
          to: to ?? undefined,
          /* the demonstration records what would go; it sends nothing */
          status: to ? (channel === "app" ? "waiting" : "not_configured") : "no_address",
          error: to
            ? channel === "app"
              ? undefined
              : "demonstration — no provider; nothing is sent"
            : channel === "sms"
              ? "no mobile number on file"
              : channel === "email"
                ? "no email on file"
                : "not signed in to the mobile app",
          event: "dispatched",
        });
      }
    this.dispatchOutbox.set(wo.id, [...(this.dispatchOutbox.get(wo.id) ?? []), ...rows]);
    return this.delay({
      workOrder: { id: wo.id, number: wo.number, status: "dispatched", crew: group.name },
      notifications: rows,
      summary: summarise(rows),
      channels: { sms: false, email: false },
    });
  }

  listDispatchGroups(_siteId: string): Promise<DispatchGroup[]> {
    return this.delay(this.dispatchGroups.map((g) => ({ ...g, members: g.members.map((m) => ({ ...m })) })));
  }

  saveDispatchGroup(_siteId: string, group: DispatchGroup): Promise<DispatchGroup> {
    const clean: DispatchGroup = {
      ...group,
      members: group.members.filter((m) => m.name.trim()).map((m) => ({ ...m, name: m.name.trim() })),
    };
    const i = this.dispatchGroups.findIndex((g) => g.key === group.key);
    if (i >= 0) this.dispatchGroups[i] = clean;
    else this.dispatchGroups.push(clean);
    return this.delay(clean);
  }

  /* ── the contact directory and who is told ─────────────────────────────── */
  private directory: ContactOrg[] = mockDirectory();
  private loosePeople: ContactPerson[] = [];
  private rules: NotifyRule[] | null = null;
  private ruleMessages = new Map<string, WorkOrderMessage[]>();

  private rulesFor(siteId: string): NotifyRule[] {
    if (!this.rules) this.rules = mockRules(siteId, this.directory);
    return this.rules;
  }

  private allPeople(): ContactPerson[] {
    return [...this.directory.flatMap((o) => o.people), ...this.loosePeople];
  }

  private reachFor(recipients: Recipient[]): Reach[] {
    const out = new Map<string, Reach>();
    for (const r of recipients) {
      if (r.type === "person") {
        const p = this.allPeople().find((x) => x.id === r.id && x.active);
        if (p) out.set(p.id!, reachOf(p));
      } else if (r.type === "org") {
        for (const p of this.directory.find((o) => o.id === r.id)?.people ?? [])
          if (p.active) out.set(p.id!, reachOf(p));
      } else {
        const g = this.dispatchGroups.find((x) => x.key === r.id);
        g?.members.forEach((m, i) =>
          out.set(`${r.id}:${i}`, {
            name: m.name,
            sms: m.phone ? [m.phone] : [],
            email: m.email ? [m.email] : [],
            app: !!m.principalId,
          }),
        );
      }
    }
    return [...out.values()];
  }

  /** The site's rules applied to one work order event, as the platform applies them (work/notices.py). */
  private applyRules(wo: WorkOrder, event: NoticeEvent): void {
    const rows = this.ruleMessages.get(wo.id) ?? [];
    const dispatchGroup = (wo.attrs as { dispatchGroup?: string } | undefined)?.dispatchGroup;
    let n = rows.length;
    for (const rule of this.rulesFor(wo.siteId)) {
      if (!rule.active || !rule.events.includes(event)) continue;
      if (!ruleMatches(rule, { priority: wo.priority, kind: wo.kind, zoneCode: wo.zoneCode, dispatchGroup })) continue;
      for (const p of this.reachFor(rule.recipients)) {
        const push = (channel: Channel, to?: string) =>
          rows.push({
            id: `${wo.id}-r${++n}`,
            recipient: p.name,
            channel,
            to,
            status: to ? (channel === "app" ? "waiting" : "not_configured") : "no_address",
            error: to
              ? channel === "app"
                ? undefined
                : "demonstration — no provider; nothing is sent"
              : "no contact on file",
            event,
            createdAt: new Date().toISOString(),
            rule: rule.name,
          });
        if (rule.channels.includes("sms")) (p.sms.length ? p.sms : [undefined]).forEach((to) => push("sms", to));
        if (rule.channels.includes("email"))
          (p.email.length ? p.email : [undefined]).forEach((to) => push("email", to));
        if (rule.channels.includes("app")) push("app", p.app ? p.name : undefined);
      }
    }
    if (event === "dispatched") this.tellContractor(wo, rows, true);
    this.ruleMessages.set(wo.id, rows);
  }

  /**
   * The contractor named on the order is told, as work/notices.py does: its people by SMS, email
   * and the app, or one row saying it is not in the directory. On the sample nobody is at the
   * other end, so when `simulate` is on the contractor's app answers by itself — delivered after
   * a few seconds, confirmed a few seconds later — and the history says it was the sample.
   */
  private tellContractor(wo: WorkOrder, rows: WorkOrderMessage[], simulate: boolean): WorkOrderMessage[] {
    const name = typeof wo.attrs?.contractor === "string" ? wo.attrs.contractor.trim() : "";
    if (!name) return [];
    const org = this.directory.find((o) => o.active && o.name.toLowerCase() === name.toLowerCase());
    const at = new Date().toISOString();
    const added: WorkOrderMessage[] = [];
    let n = rows.length;
    const push = (m: Omit<WorkOrderMessage, "id" | "event" | "createdAt">) => {
      const row = { ...m, id: `${wo.id}-c${++n}`, event: "dispatched", createdAt: at };
      rows.push(row);
      added.push(row);
      return row;
    };
    if (!org) {
      push({ recipient: name, channel: "sms", status: "no_address", error: `${name} is not in the contact directory` });
      return added;
    }
    const told = new Set(rows.filter((r) => r.event === "dispatched").map((r) => r.recipient));
    for (const p of org.people.filter((x) => x.active && !told.has(x.name))) {
      const r = reachOf(p);
      for (const to of r.sms)
        push({ recipient: p.name, channel: "sms", to, status: "not_configured", error: "demonstration — no provider; nothing is sent" });
      for (const to of r.email)
        push({ recipient: p.name, channel: "email", to, status: "not_configured", error: "demonstration — no provider; nothing is sent" });
      const app = push({ recipient: p.name, channel: "app", status: "waiting" });
      if (simulate && typeof window !== "undefined") {
        window.setTimeout(() => {
          if (app.status === "waiting") Object.assign(app, { status: "delivered", sentAt: new Date().toISOString() });
        }, 4_000);
        window.setTimeout(() => {
          if (wo.status !== "dispatched" || wo.receivedAt) return;
          Object.assign(app, { status: "read", readAt: new Date().toISOString() });
          this.receive(wo, "app", p.name, "the sample's contractor app answered by itself");
        }, 12_000);
      }
    }
    return added;
  }

  /** Record a receipt: the first is kept on the order; each one is a history line. */
  private receive(wo: WorkOrder, via: ReceiptVia, by?: string, note?: string, at = new Date().toISOString()): void {
    if (!wo.receivedAt) Object.assign(wo, { receivedAt: at, receivedVia: via, receivedBy: by });
    const words = { app: "in the mobile app", link: "by the confirm link", phone: "by phone", email: "by email", in_person: "in person", other: "" }[via];
    this.history.get(wo.id)?.push({
      id: `${wo.id}-rcv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      workOrderId: wo.id,
      at,
      by: this.principal,
      from: wo.status,
      to: wo.status,
      note: `Received${by ? ` by ${by}` : ""} ${words}`.trim() + (note ? `: ${note}` : ""),
    });
  }

  /** The sample's dispatched orders, one in each delivery state, so every color can be seen. */
  private seedDelivery(): void {
    const byNumber = (n: number) => this.orders.find((w) => w.number === `WO-${String(n).padStart(6, "0")}`);
    const at = (w: WorkOrder) => new Date(Date.parse(w.openedAt) + 3 * 3_600_000).toISOString();
    for (const w of this.orders.filter((x) => x.status === "dispatched")) {
      const rows: WorkOrderMessage[] = [];
      this.tellContractor(w, rows, false);
      this.ruleMessages.set(w.id, rows);
    }
    const setApp = (n: number, status: string) =>
      (this.ruleMessages.get(byNumber(n)?.id ?? "") ?? []).filter((m) => m.channel === "app").forEach((m) => (m.status = status));
    /* WO-3 received by phone, WO-5 delivered to the app, WO-16 still on its way, WO-19 not in the directory */
    const three = byNumber(3);
    if (three) {
      setApp(3, "read");
      this.receive(three, "phone", "Airfield maintenance supervisor", undefined, at(three));
    }
    setApp(5, "delivered");
    /* work already started: the crew had it */
    for (const n of [1, 15]) {
      const w = byNumber(n);
      if (w) this.receive(w, "app", w.crew, undefined, at(w));
    }
  }

  /* ── closing and verifying: photos, the site's switch, the contractor's link ── */
  private workSettings = new Map<string, WorkSettings>();

  async getWorkSettings(siteId: string): Promise<WorkSettings> {
    return this.delay({ ...(this.workSettings.get(siteId) ?? { autoVerifyPhoto: true }) });
  }

  async setWorkSettings(siteId: string, settings: WorkSettings): Promise<WorkSettings> {
    this.workSettings.set(siteId, { ...settings });
    return this.delay({ ...settings });
  }

  /** As workorders.auto_verify: a completed order with an after photo is verified by itself, if the site allows. */
  private autoVerify(wo: WorkOrder): void {
    if (wo.status !== "completed") return;
    if (!(this.workSettings.get(wo.siteId) ?? { autoVerifyPhoto: true }).autoVerifyPhoto) return;
    if (!(wo.evidence ?? []).some((e) => e.phase === "after")) return;
    const at = new Date().toISOString();
    Object.assign(wo, { status: "verified", verifiedAt: at, verifiedBy: undefined, verifiedVia: "photo" });
    this.history.get(wo.id)?.push({
      id: `${wo.id}-av-${Date.now()}`,
      workOrderId: wo.id,
      at,
      by: "Platform",
      from: "completed",
      to: "verified",
      note: "Verified automatically: an after photograph is on file",
    });
    this.applyRules(wo, "verified");
  }

  async addWorkOrderNote(id: string, text: string): Promise<void> {
    const wo = this.find(id);
    const note = text.trim();
    if (!note) throw new DataSourceError("the note is empty", { status: 400 });
    this.history.get(id)?.push({
      id: `${id}-note-${Date.now()}`,
      workOrderId: id,
      at: new Date().toISOString(),
      by: this.principal,
      from: wo.status,
      to: wo.status,
      note,
      kind: "note",
    });
    return this.delay(undefined);
  }

  async uploadWorkOrderPhoto(id: string, file: File, phase: "before" | "after"): Promise<WorkOrder> {
    const wo = this.find(id);
    wo.evidence = [
      ...(wo.evidence ?? []),
      { id: `${id}-ph-${Date.now()}`, phase, createdAt: new Date().toISOString(), contentType: file.type || "image/jpeg" },
    ];
    if (phase === "after") this.autoVerify(wo);
    return this.delay(this.withTransitions(wo));
  }

  /** The contractor's job link, played on the sample: received, start, and done with a photo. */
  async simulateContractor(id: string, action: ContractorAction, photo?: File, note?: string): Promise<WorkOrder> {
    const wo = this.find(id);
    const who = (this.ruleMessages.get(id) ?? []).find((m) => m.event === "dispatched" && m.channel === "app")?.recipient
      ?? (typeof wo.attrs?.contractor === "string" ? wo.attrs.contractor : "Contractor");
    if (!wo.receivedAt) this.receive(wo, "link", who);
    /* as confirm_by_token: that person's messages are read */
    for (const m of this.ruleMessages.get(id) ?? [])
      if (m.recipient === who && m.event === "dispatched" && m.status === "waiting") Object.assign(m, { status: "read" });
    if (action === "start" || action === "done") {
      if (wo.status === "dispatched") {
        await this.transitionWorkOrder(id, { to: "in_progress", note: `Started by ${who} from the job link` });
      }
    }
    if (action === "done") {
      if (photo)
        wo.evidence = [
          ...(wo.evidence ?? []),
          { id: `${id}-ph-${Date.now()}`, phase: "after", createdAt: new Date().toISOString(), contentType: photo.type || "image/jpeg" },
        ];
      await this.transitionWorkOrder(id, {
        to: "completed",
        note: `Closed by ${who} from the job link${note?.trim() ? `: ${note.trim()}` : ""}`,
      });
      Object.assign(wo, { completedBy: who, completedByContact: who });
    }
    return this.delay(this.withTransitions(wo));
  }

  async confirmWorkOrderReceipt(id: string, input: ReceiptInput): Promise<WorkOrder> {
    const wo = this.find(id);
    if (!(["app", "link", "phone", "email", "in_person", "other"] as string[]).includes(input.via))
      throw new DataSourceError("via must be one of app, link, phone, email, in_person, other", { status: 400 });
    this.receive(wo, input.via, input.by?.trim() || undefined, input.note?.trim() || undefined);
    return this.delay(this.withTransitions(wo));
  }

  /* ── the flight record (AODB) ───────────────────────────────────────── */
  private aodbMoves: Movement[] | null = null;
  private aodbEvents = new Map<string, MovementEvent[]>();
  private aodbInbox: MovementMessage[] = [];
  private aodbLines: ScheduleLine[] = [];

  private aodbState(): Movement[] {
    if (!this.aodbMoves) {
      /* the sample day, and the rolling hours of traffic around now */
      const flights = [...mockFlights(), ...mockWave(new Date())];
      this.aodbFlights = flights;
      this.aodbMoves = mockMovements(flights);
      this.aodbHistory = mockHistory(flights);
      this.aodbInbox = mockInbox(this.aodbMoves);
      this.aodbLines = mockSchedule(this.aodbMoves);
    }
    this.aodbMoves = runDay(this.aodbMoves, this.aodbHistory, biasOf, turnOf, new Date());
    return this.aodbMoves;
  }
  private aodbHistory: Past[] = [];
  private aodbFlights: Flight[] = [];

  private aodbWrite(
    id: string,
    changes: Partial<Movement>,
    source: string,
    note?: string,
  ): { movement: Movement; changed: string[] } {
    const moves = this.aodbState();
    const i = moves.findIndex((m) => m.id === id);
    if (i < 0) throw new DataSourceError("no such movement", { status: 404 });
    const before = moves[i];
    const changed = (Object.keys(changes) as (keyof Movement)[]).filter(
      (k) => JSON.stringify(before[k]) !== JSON.stringify(changes[k]),
    );
    if (!changed.length) return { movement: before, changed: [] };
    const after = { ...before, ...changes, version: before.version + 1 };
    const marks = provenanceMarks(before, changes, changed, source, source === "manual" ? this.principal : undefined);
    if (Object.keys(marks).length) after.provenance = { ...(before.provenance ?? {}), ...marks };
    moves[i] = after;
    const ev = this.aodbEvents.get(id) ?? [];
    const now = new Date().toISOString();
    for (const k of changed)
      ev.push({
        at: now,
        field: String(k),
        old: before[k] == null ? undefined : JSON.stringify(before[k]),
        new: JSON.stringify(after[k]),
        source,
        by: source === "manual" ? this.principal : undefined,
        note,
      });
    this.aodbEvents.set(id, ev);
    this.aodbMoves = runDay(moves, this.aodbHistory, biasOf, turnOf, new Date());
    return { movement: this.aodbMoves.find((m) => m.id === id)!, changed: changed.map(String) };
  }

  aodbDay(_siteId: string, date?: string): Promise<{ serviceDate: string; covered: boolean; items: Movement[] }> {
    const moves = this.aodbState();
    const d = date ?? moves[0]?.serviceDate ?? new Date().toISOString().slice(0, 10);
    return this.delay({
      serviceDate: d,
      covered: true,
      items: structuredClone(moves.filter((m) => !date || m.serviceDate === date)),
    });
  }

  aodbSummary(_siteId: string, date?: string): Promise<DaySummary> {
    const moves = this.aodbState();
    return this.delay(summariseDay(moves, date ?? moves[0]?.serviceDate ?? ""));
  }

  async aodbMovement(
    id: string,
  ): Promise<{ movement: Movement; history: MovementEvent[]; messages: MovementMessage[] }> {
    const m = this.aodbState().find((x) => x.id === id);
    if (!m) throw new DataSourceError("no such movement", { status: 404 });
    const hand = this.aodbAddedNotes.get(id);
    const created: MovementEvent = hand
      ? { at: hand.at, field: "created", new: m.scheduledAt, source: "manual", by: hand.by, note: hand.note }
      : {
          at: m.scheduledAt ?? "",
          field: "created",
          new: m.scheduledAt,
          source: "schedule",
          note: "from the seasonal schedule",
        };
    const predicted: MovementEvent[] = m.prediction
      ? [
          {
            at: m.prediction.at,
            field: "predicted_at",
            new: m.predictedAt,
            source: "predictor",
            note: m.prediction.basis.join("; "),
          },
        ]
      : [];
    return this.delay(
      structuredClone({
        movement: m,
        history: [created, ...(this.aodbEvents.get(id) ?? []), ...predicted],
        messages: this.aodbInbox.filter((x) => x.movementId === id),
      }),
    );
  }

  async aodbChange(
    id: string,
    changes: Partial<Movement>,
    version?: number,
    note?: string,
  ): Promise<{ movement: Movement; changed: string[] }> {
    const m = this.aodbState().find((x) => x.id === id);
    if (!m) throw new DataSourceError("no such movement", { status: 404 });
    if (version !== undefined && version !== m.version)
      throw new DataSourceError(
        `the record changed since you opened it (version ${m.version}, you had ${version}); reload and apply your change again`,
        { status: 409 },
      );
    return this.delay(this.aodbWrite(id, changes, "manual", note));
  }

  /**
   * A delay notice, as record.add_notice keeps it: the estimate moves to the
   * time given, the code (if any) goes first, the note becomes the remarks,
   * and the notice is appended. A flight that has flown or is cancelled
   * takes no notice.
   */
  async aodbNotice(
    _siteId: string,
    movementId: string,
    n: { source: string; newTime: string; code?: string; note?: string },
    version?: number,
  ): Promise<{ movement: Movement; changed: string[] }> {
    const sources = Object.keys(DELAY_SOURCE_LABEL);
    if (!sources.includes(n.source))
      throw new DataSourceError(`source is one of ${sources.join(", ")}`, { status: 400 });
    if (!n.newTime) throw new DataSourceError("a delay notice gives the new time", { status: 400 });
    const at = Date.parse(n.newTime);
    if (Number.isNaN(at)) throw new DataSourceError(`'${n.newTime}' is not a time`, { status: 400 });
    const m = this.aodbState().find((x) => x.id === movementId);
    if (!m) throw new DataSourceError("no such movement", { status: 404 });
    if (m.status === "cancelled" || m.cancelledReason) throw new DataSourceError("that flight is cancelled", { status: 400 });
    if (m.actualAt) throw new DataSourceError("that flight has already happened", { status: 400 });
    if (version !== undefined && version !== m.version)
      throw new DataSourceError(
        `the record changed since you opened it (version ${m.version}, you had ${version}); reload and apply your change again`,
        { status: 409 },
      );
    const code = n.code?.trim() || undefined;
    const note = n.note?.trim() || undefined;
    const newTime = new Date(at).toISOString();
    const notice = {
      at: new Date().toISOString(),
      source: n.source,
      by: this.principal,
      newTime,
      ...(code ? { code } : {}),
      ...(note ? { note } : {}),
    };
    const changes: Partial<Movement> = { estimatedAt: newTime, notices: [...(m.notices ?? []), notice] };
    if (code) changes.delayCodes = [{ code, source: "entered" }, ...m.delayCodes.filter((c) => c.code !== code)];
    if (note) changes.remarks = note;
    const label = DELAY_SOURCE_LABEL[n.source as DelaySource];
    return this.delay(this.aodbWrite(movementId, changes, "manual", `delay notice from ${label}${note ? `: ${note}` : ""}`));
  }

  /* ── build 6: the flight-record figures a person enters by hand ──
   * Checked by domain/flightEntries.ts, the twin of the platform's
   * movements/entries.py, so the demo takes and refuses the same entries in
   * the same words. Kept on this page's copy of the record, as every write
   * to the sample is. */
  private aodbAddedNotes = new Map<string, { at: string; by?: string; note: string }>();
  private aodbAddSeq = 0;

  private aodbOpen(id: string, version?: number): Movement {
    const m = this.aodbState().find((x) => x.id === id);
    if (!m) throw new DataSourceError("no such movement", { status: 404 });
    if (version !== undefined && version !== m.version)
      throw new DataSourceError(
        `the record changed since you opened it (version ${m.version}, you had ${version}); reload and enter it again`,
        { status: 409 },
      );
    return m;
  }

  private aodbEntry(
    id: string,
    version: number | undefined,
    check: (m: Movement) => EntryResult,
  ): Promise<{ movement: Movement; changed: string[]; replaced: { field: string; source: string }[] }> {
    const m = this.aodbOpen(id, version);
    let r: EntryResult;
    try {
      r = check(m);
    } catch (e) {
      if (e instanceof EntryRefused) throw new DataSourceError(e.message, { status: 400 });
      throw e;
    }
    const out = this.aodbWrite(id, r.changes, "manual", r.note);
    return this.delay({ ...structuredClone(out), replaced: r.replaced });
  }

  async aodbAdd(_siteId: string, d: NewMovement): Promise<Movement> {
    let c: ReturnType<typeof checkNew>;
    try {
      c = checkNew(d, new Date());
    } catch (e) {
      if (e instanceof EntryRefused) throw new DataSourceError(e.message, { status: 400 });
      throw e;
    }
    const moves = this.aodbState();
    if (moves.some((m) => m.carrier === c.carrier && m.number === c.number && m.direction === c.direction && m.serviceDate === c.serviceDate))
      throw new DataSourceError(`${c.carrier}${c.number} ${c.direction} is already on the record for ${c.serviceDate}`, {
        status: 409,
      });
    const id = `mv-hand-${Date.now().toString(36)}-${++this.aodbAddSeq}`;
    const m: Movement = {
      id,
      direction: c.direction,
      carrier: c.carrier,
      number: c.number,
      registration: c.registration,
      classKey: c.classKey,
      counterpartNode: c.counterpartNode,
      serviceType: c.serviceType,
      serviceDate: c.serviceDate,
      status: "scheduled",
      scheduledAt: c.scheduledAt,
      milestones: {},
      handling: {},
      loads: (c.loads ?? {}) as Record<string, number>,
      ...(c.capacity ? { capacity: c.capacity } : {}),
      delayCodes: [],
      notices: [],
      flags: [],
      version: 1,
      source: "manual",
    };
    moves.push(m);
    /* the stand plan sizes it from its type, as the platform's engine does */
    this.aodbFlights.push({
      id,
      direction: c.direction,
      carrier: c.carrier,
      number: c.number,
      scheduledAt: c.scheduledAt,
      source: "manual",
      size: sizeOfClass(c.classKey),
    });
    this.aodbAddedNotes.set(id, { at: new Date().toISOString(), by: this.principal, note: c.note });
    this.aodbMoves = runDay(moves, this.aodbHistory, biasOf, turnOf, new Date());
    return this.delay(structuredClone(this.aodbMoves.find((x) => x.id === id)!));
  }

  aodbActuals(id: string, times: Partial<Record<ActualKey, string>>, version?: number, note?: string) {
    return this.aodbEntry(id, version, (m) => checkActuals(m, times, new Date(), note));
  }

  aodbLoads(
    id: string,
    loads: Partial<Record<string, number | null>>,
    opts: { capacity?: number | null; version?: number; note?: string } = {},
  ) {
    return this.aodbEntry(id, opts.version, (m) =>
      checkLoads(m, loads, { given: "capacity" in opts, value: opts.capacity }, opts.note),
    );
  }

  aodbDelayCodes(id: string, codes: { code: string; minutes?: number }[], version?: number, note?: string) {
    return this.aodbEntry(id, version, (m) => checkDelayCodes(m, codes, note));
  }

  aodbTobt(id: string, tobt: string, opts: { givenBy?: string; version?: number; note?: string } = {}) {
    return this.aodbEntry(id, opts.version, (m) => checkTobt(m, tobt, new Date(), opts.givenBy, opts.note));
  }

  aodbSchedule(_siteId: string): Promise<ScheduleLine[]> {
    this.aodbState();
    return this.delay(structuredClone(this.aodbLines));
  }

  aodbImportSsim(_siteId: string, ssim: string): Promise<{ legs: number; lines: number; skipped: number }> {
    const legs = ssim.split(/\r?\n/).filter((l) => l.startsWith("3") && l.length >= 60);
    return this.delay({ legs: legs.length, lines: legs.filter((l) => l.includes("MED")).length, skipped: 0 });
  }

  aodbGenerate(
    _siteId: string,
    _from: string,
    _to: string,
  ): Promise<{ created: number; alreadyThere: number; lines: number }> {
    this.aodbState();
    return this.delay({ created: 0, alreadyThere: this.aodbMoves!.length, lines: this.aodbLines.length });
  }

  async aodbPostMessage(
    _siteId: string,
    kind: "mvt" | "aidx",
    raw: string,
  ): Promise<{ outcome: MovementMessage["outcome"]; refusal?: string; changed: string[] }> {
    const moves = this.aodbState();
    const received = new Date().toISOString();
    const out =
      kind === "mvt"
        ? applyMvt(raw, moves, ["MED", "OEMA"])
        : ({ outcome: "refused", refusal: "the sample reads MVT only" } as const);
    let changed: string[] = [];
    if (out.outcome === "applied")
      changed = this.aodbWrite(out.movementId, out.changes, "mvt", "from a movement message").changed;
    const m = "movementId" in out && out.movementId ? moves.find((x) => x.id === out.movementId) : undefined;
    this.aodbInbox.unshift({
      id: `msg-${Date.now().toString(36)}`,
      kind,
      raw,
      receivedAt: received,
      outcome: out.outcome,
      refusal: out.outcome === "applied" ? undefined : out.refusal,
      changes: changed,
      movement: m ? `${m.carrier}${m.number} ${m.direction}` : undefined,
      movementId: m?.id,
    });
    return this.delay({ outcome: out.outcome, refusal: out.outcome === "applied" ? undefined : out.refusal, changed });
  }

  aodbMessages(_siteId: string): Promise<MovementMessage[]> {
    this.aodbState();
    return this.delay(structuredClone(this.aodbInbox));
  }

  aodbPredict(_siteId: string): Promise<{ predicted: number; moved: number; flagged: number }> {
    const moves = this.aodbState();
    return this.delay({
      predicted: moves.filter((m) => m.prediction).length,
      moved: 0,
      flagged: moves.filter((m) => m.flags.length).length,
    });
  }

  /*
   * ── flight data feeds, sample ──────────────────────────────────────
   * Three sample feeds and their log, marked sample. Saving and testing work
   * on the page's own copy; nothing is applied to the sample flights, which is
   * what "sample" says. The test reads a message only as far as naming what it
   * is — the platform does the reading for real.
   */
  private feeds: { items: FlightFeed[]; messages: FeedMessage[] } | null = null;

  private feedState() {
    if (!this.feeds) {
      const now = Date.now();
      this.feeds = { items: mockFeeds(now), messages: mockFeedMessages(now) };
    }
    return this.feeds;
  }

  flightFeeds(_siteId: string): Promise<{ items: FlightFeed[]; sample: boolean }> {
    return this.delay(structuredClone({ items: this.feedState().items, sample: true }));
  }

  flightFeedMessages(_siteId: string, opts?: { feedId?: string; outcome?: FeedOutcome }): Promise<FeedMessage[]> {
    const rows = this.feedState().messages.filter(
      (m) => (!opts?.feedId || m.feedId === opts.feedId) && (!opts?.outcome || m.outcome === opts.outcome),
    );
    return this.delay(structuredClone(rows));
  }

  async saveFlightFeed(_siteId: string, draft: FeedDraft, id?: string): Promise<FlightFeed> {
    const st = this.feedState();
    for (const [k, v] of Object.entries(draft.config.secretRefs ?? {}))
      if (v && !/^SLT_FEED_[A-Z0-9_]{1,60}$/.test(v))
        throw new DataSourceError(`secret_refs.${k} must name an environment variable SLT_FEED_… — never the secret itself`, {
          status: 400,
        });
    const secrets = Object.fromEntries(
      Object.entries(draft.config.secretRefs ?? {}).map(([k, v]) => [k, { name: v, set: false }]),
    );
    if (id) {
      const f = st.items.find((x) => x.id === id);
      if (!f) throw new DataSourceError("no such feed", { status: 404 });
      Object.assign(f, { name: draft.name, enabled: draft.enabled, config: { ...f.config, ...draft.config }, secrets });
      f.status.state = draft.enabled ? f.status.state === "off" ? "quiet" : f.status.state : "off";
      return this.delay(structuredClone(f));
    }
    if (st.items.some((x) => x.key === draft.key)) throw new DataSourceError(`there is already a feed called ${draft.key}`, { status: 400 });
    const path = draft.kind === "aidx" ? "/v1/aodb/aidx" : draft.kind === "typeb" ? "/v1/aodb/typeb" : undefined;
    const feed: FlightFeed = {
      id: `feed-${draft.key}`,
      key: draft.key,
      name: draft.name,
      kind: draft.kind,
      transport: draft.transport,
      enabled: draft.enabled,
      config: draft.config,
      secrets,
      pushPath: path && draft.transport === "push" ? `${path}?feed=${draft.key}` : undefined,
      status: {
        state: draft.enabled ? "quiet" : "off",
        why: "no message yet",
        perHour: 0,
        last24h: 0,
        outcomes24h: { taken: 0, held: 0, stored: 0, duplicate: 0, refused: 0 },
        refusals24h: [],
      },
    };
    st.items.push(feed);
    return this.delay(structuredClone(feed));
  }

  async testFlightFeed(id: string, sample?: string): Promise<FeedTestResult> {
    const f = this.feedState().items.find((x) => x.id === id);
    if (!f) throw new DataSourceError("no such feed", { status: 404 });
    if (!sample)
      return this.delay({
        ok: f.status.state !== "failing",
        error: f.status.state === "failing" ? f.lastError : undefined,
        note: f.transport === "push" ? "a push feed is tested by sending a sample message; nothing is fetched" : undefined,
        secrets: f.secrets,
        results: [],
      });
    const text = sample.trim();
    let result: FeedResult;
    if (/<!DOCTYPE|<!ENTITY/i.test(text))
      result = { index: 0, outcome: "refused", reason: "a DOCTYPE or entity declaration is refused" };
    else if (f.kind === "aidx")
      result = /IATA_AIDX_FlightLegNotifRQ/.test(text) && /<\/IATA_AIDX_FlightLegNotifRQ>\s*$/.test(text)
        ? { index: 0, outcome: "held", reason: "sample: read as AIDX; the sample flights are not changed by a test" }
        : { index: 0, outcome: "refused", reason: `no IATA_AIDX_FlightLegNotifRQ in the document` };
    else {
      const smi = text
        .split(/\r?\n/)
        .map((l) => l.trim().toUpperCase())
        .find((l) => /^(MVT|MVA|LDM|ASM|SSM)$/.test(l));
      result = smi
        ? { index: 0, outcome: "held", reason: `sample: read as ${smi}; the sample flights are not changed by a test` }
        : { index: 0, outcome: "refused", reason: "no message type where the message begins — garbled, or not Type B" };
    }
    return this.delay({ ok: true, secrets: f.secrets, results: [result] });
  }

  async replayFeedMessage(id: string): Promise<{ results: FeedResult[] }> {
    const st = this.feedState();
    const m = st.messages.find((x) => x.id === id);
    if (!m) throw new DataSourceError("no such message", { status: 404 });
    /* in the sample a held message's flight "has since been loaded", so its replay is taken */
    const outcome: FeedOutcome = m.outcome === "held" ? "taken" : m.outcome === "refused" ? "refused" : "duplicate";
    const again: FeedMessage = {
      ...m,
      id: `fm-${Date.now().toString(36)}`,
      channel: "replay",
      outcome,
      reason: outcome === "taken" ? undefined : m.reason,
      replayOf: m.id,
      receivedAt: new Date().toISOString(),
      changes: outcome === "taken" ? ["status"] : [],
    };
    st.messages.unshift(again);
    return this.delay({ results: [{ index: 0, outcome, reason: again.reason, messageId: again.id }] });
  }

  /* ── resource allocation ───────────────────────────────────────────── */
  private alloc: {
    plan: Assignment[];
    rules: AllocRule[];
    policies: Record<ResourceKind, Policy>;
    learned: Learned[];
    log: AllocLogEntry[];
    proposals: Proposal[];
    trust: Trust;
  } | null = null;
  private allocSeq = 0;
  /** stands, gates, desks and belts added by hand in the demo — kept beside the sample estate */
  private allocAdded: Resource[] = [];
  /** build 6: changes made to the register in the demo, by code, laid over the sample estate */
  private allocEdits = new Map<string, Partial<Resource> & { active?: boolean }>();

  /** The register as kept: the sample estate, what was added, every change laid over it; inactive ones marked. */
  private allocRegisterAll(): (Resource & { active: boolean })[] {
    const all = [...mockResources(mockStands()), ...structuredClone(this.allocAdded)];
    return all.map((r) => {
      const e = this.allocEdits.get(r.code);
      const out: Resource & { active: boolean } = { ...r, active: true };
      if (e)
        for (const [k, v] of Object.entries(e)) {
          if (v === null || v === undefined) delete (out as unknown as Record<string, unknown>)[k];
          else (out as unknown as Record<string, unknown>)[k] = v;
        }
      return out;
    });
  }

  /** The engine's input, built from the flight record as it is now. */
  private allocInput(now = new Date()): EngineInput {
    const moves = this.aodbState();
    const size = new Map(this.aodbFlights.map((f) => [f.id, f.size ?? "C"]));
    const sizeOf = (m: Movement) => size.get(m.id) ?? "C";
    const register = mockStands();
    const resources: Resource[] = this.allocRegisterAll()
      .filter((r) => r.active)
      .map(({ active: _a, ...r }) => r);
    /* the rest of the platform closes stands: open work orders that name one — added stands included */
    const added = this.allocAdded
      .filter((r) => r.kind === "position")
      .map((r) => ({ code: r.code, kind: "remote" as const, maxSize: r.maxSize ?? "C", zoneCode: r.group }));
    const blocks = blocksFromWorkOrders([...register.stands, ...added], this.orders).filter((b) => b.severity === "closed");
    for (const b of blocks) {
      const r = resources.find((x) => x.kind === "position" && x.code === b.standCode);
      if (r) r.closed = [...(r.closed ?? []), { from: b.from, to: b.to, why: b.why, ref: b.ref }];
    }
    if (!this.alloc) {
      /*
       * The overnight plan: made on the schedule alone, the sample stand plan
       * kept exactly as it was entered, the rest placed by the engine. The
       * day then judges it against what the flight record now expects.
       */
      const sched = moves.map((m) => ({
        ...m,
        estimatedAt: undefined,
        predictedAt: undefined,
        prediction: undefined,
        actualAt: undefined,
        milestones: {},
      }));
      const dSched = demandsFromMovements(sched, sizeOf, now);
      const locked = assignmentsFromStands(register, dSched).map((a) => ({ ...a, locked: true }));
      const policies = mockPolicies();
      const rules = mockAllocRules();
      const learned = mockLearned();
      const night = solve({ resources, demands: dSched, rules, learned, policies, current: locked, now, mode: "plan", explain: false });
      this.alloc = {
        plan: night.assignments.map(({ locked: _l, ...a }) => ({ ...a, source: a.source === "plan" ? "plan" : "engine" })),
        rules,
        policies,
        learned,
        log: [],
        proposals: [],
        trust: { proposed: 0, applied: 0, rejected: 0, auto: 0 },
      };
    }
    const a = this.alloc;
    return {
      resources,
      demands: demandsFromMovements(moves, sizeOf, now),
      rules: a.rules,
      learned: a.learned,
      policies: a.policies,
      current: a.plan,
      now,
      mode: "replan",
    };
  }

  private allocLog(result: EngineResult, source: AllocLogEntry["source"], by?: string) {
    const at = new Date().toISOString();
    for (const m of result.moves)
      this.alloc!.log.unshift({ at, kind: m.kind, label: m.label, from: m.from, to: m.to, source, by, why: m.why });
    this.alloc!.log = this.alloc!.log.slice(0, 200);
  }

  private allocProposal(result: EngineResult, trigger: string): Proposal {
    return {
      id: `prop-${++this.allocSeq}`,
      createdAt: new Date().toISOString(),
      trigger,
      moves: result.moves,
      before: result.before ?? result.score,
      after: result.score,
      decision: decide(result, this.alloc!.policies),
      status: "pending",
      assignments: result.assignments,
    };
  }

  /**
   * Re-plan what the policies let the engine fix alone, then propose the
   * rest. Kinds set to "auto" fix themselves — each within its own limits —
   * and anything else waits for a person, with the reason.
   */
  private allocTick(): void {
    const input = this.allocInput();
    const a = this.alloc!;
    const autoKinds = (Object.keys(a.policies) as ResourceKind[]).filter((k) => a.policies[k].automation === "auto");
    if (autoKinds.length) {
      const r = solve({ ...input, kinds: autoKinds, explain: false });
      if (r.moves.length && decide(r, a.policies).apply) {
        a.plan = applyMoves(r, "auto");
        this.allocLog(r, "auto");
        const p = this.allocProposal(r, "automatic");
        a.proposals.unshift({ ...p, status: "auto_applied", decidedBy: "engine", decidedAt: p.createdAt });
        a.trust.auto += 1;
      }
    }
    const pending = a.proposals.find((p) => p.status === "pending");
    const now = assess({ ...this.allocInput(), explain: false });
    if (!now.issues.length) {
      if (pending) pending.status = "expired";
      return;
    }
    const r = solve({ ...this.allocInput(), explain: false });
    if (!r.moves.length) return;
    const sig = (moves: { key: string; to: string[] }[]) => moves.map((m) => `${m.key}>${m.to.join()}`).join("|");
    if (pending && sig(pending.moves) === sig(r.moves)) return;
    if (pending) pending.status = "expired";
    a.proposals.unshift(this.allocProposal(r, "the plan no longer holds"));
    a.trust.proposed += 1;
  }

  allocDay(_siteId: string): Promise<AllocDay> {
    this.allocTick();
    const input = this.allocInput();
    const a = this.alloc!;
    const judged = assess(input);
    const times = input.demands.map((d) => d.from).sort();
    return this.delay(
      structuredClone({
        now: input.now.toISOString(),
        horizon: { from: times[0] ?? input.now.toISOString(), to: input.demands.map((d) => d.to).sort().at(-1) ?? input.now.toISOString() },
        resources: [...input.resources],
        demands: [...input.demands],
        plan: a.plan,
        explain: judged.explain,
        issues: judged.issues,
        score: judged.score,
        proposal: a.proposals.find((p) => p.status === "pending") ?? null,
        policies: Object.values(a.policies),
        rules: a.rules,
        learned: a.learned,
        log: a.log,
        trust: a.trust,
        sample: true,
      }),
    );
  }

  async allocReplan(_siteId: string, mode: "replan" | "plan"): Promise<Proposal> {
    const input = this.allocInput();
    const a = this.alloc!;
    const r = solve({ ...input, mode, explain: false });
    for (const p of a.proposals) if (p.status === "pending") p.status = "expired";
    const p = this.allocProposal(r, mode === "plan" ? "optimise the whole day" : "asked for a re-plan");
    a.proposals.unshift(p);
    a.trust.proposed += 1;
    return this.delay(structuredClone(p));
  }

  async allocDecide(_siteId: string, proposalId: string, decision: "apply" | "reject", note?: string): Promise<{ applied: number }> {
    const a = this.alloc;
    const p = a?.proposals.find((x) => x.id === proposalId);
    if (!a || !p) throw new DataSourceError("no such proposal", { status: 404 });
    if (p.status !== "pending") throw new DataSourceError(`that proposal is ${p.status.replace("_", " ")}`, { status: 409 });
    p.decidedBy = this.principal;
    p.decidedAt = new Date().toISOString();
    p.note = note;
    if (decision === "reject") {
      p.status = "rejected";
      a.trust.rejected += 1;
      return this.delay({ applied: 0 });
    }
    const moved = new Set(p.moves.map((m) => m.key));
    a.plan = p.assignments.map((x) => (moved.has(x.key) ? { ...x, source: "engine" as const } : x));
    p.status = "applied";
    a.trust.applied += 1;
    const at = p.decidedAt;
    for (const m of p.moves)
      a.log.unshift({ at, kind: m.kind, label: m.label, from: m.from, to: m.to, source: "engine", by: this.principal, why: m.why });
    return this.delay({ applied: p.moves.length });
  }

  async allocAssign(
    _siteId: string,
    key: string,
    codes: string[],
    opts: { note?: string; force?: boolean } = {},
  ): Promise<{ refused: Refusal[]; learned?: string }> {
    const input = this.allocInput();
    const a = this.alloc!;
    const d = input.demands.find((x) => x.key === key);
    if (!d) throw new DataSourceError("that flight is not on today's plan", { status: 404 });
    const refused = checkPlacement(input, key, codes);
    if (refused.length && !opts.force) return this.delay({ refused });
    const was = a.plan.find((x) => x.key === key);
    a.plan = [
      ...a.plan.filter((x) => x.key !== key),
      { key, kind: d.kind, segments: [{ codes, from: d.from, to: d.to }], source: "person" },
    ];
    a.learned = learnFrom(a.learned, d, codes[0]);
    a.log.unshift({
      at: new Date().toISOString(),
      kind: d.kind,
      label: d.label,
      from: was ? was.segments.map((s) => s.codes.join("+")) : [],
      to: [codes.join("+")],
      source: "person",
      by: this.principal,
      why: opts.note || (refused.length ? `placed over: ${refused.map((r) => r.why).join("; ")}` : "placed by hand"),
    });
    /* a stand change carries its gate: re-derived at once, not left at the old stand's */
    if (d.kind === "position") {
      const g = solve({ ...this.allocInput(), kinds: ["gate"], explain: false });
      if (g.moves.length) {
        a.plan = applyMoves(g, "engine");
        this.allocLog(g, "engine", this.principal);
      }
    }
    for (const p of a.proposals) if (p.status === "pending") p.status = "expired";
    return this.delay({ refused, learned: `${d.operator} → ${codes[0]}` });
  }

  async allocRelease(_siteId: string, key: string, note?: string): Promise<{ released: string }> {
    const input = this.allocInput();
    const a = this.alloc!;
    const was = a.plan.find((x) => x.key === key);
    if (!was) throw new DataSourceError("nothing is placed for that flight", { status: 404 });
    a.plan = a.plan.filter((x) => x.key !== key);
    a.log.unshift({
      at: new Date().toISOString(),
      kind: was.kind,
      label: input.demands.find((d) => d.key === key)?.label ?? key,
      from: was.segments.map((s) => s.codes.join("+")),
      to: [],
      source: "person",
      by: this.principal,
      why: note?.trim() || "released by hand",
    });
    /* as on the platform, the gate follows: with no stand, a bus gate, not the stand it left */
    if (was.kind === "position") {
      const g = solve({ ...this.allocInput(), kinds: ["gate"], explain: false });
      if (g.moves.length) {
        a.plan = applyMoves(g, "engine");
        this.allocLog(g, "engine", this.principal);
      }
    }
    for (const p of a.proposals) if (p.status === "pending") p.status = "expired";
    return this.delay({ released: key });
  }

  async allocAddResource(_siteId: string, item: Resource): Promise<{ saved: string }> {
    const code = item.code?.trim();
    if (!ALLOC_KINDS.includes(item.kind))
      throw new DataSourceError(`kind is one of ${ALLOC_KINDS.join(", ")}`, { status: 400 });
    if (!code) throw new DataSourceError("a resource has a code", { status: 400 });
    if (this.allocInput().resources.some((r) => r.code === code))
      throw new DataSourceError(`${code} is already in the register`, { status: 409 });
    /* one taken off the register comes back as now given, as the platform revives it */
    if (this.allocRegisterAll().some((r) => r.code === code && !r.active)) {
      const { kind: _k, code: _c, ...rest } = structuredClone(item);
      this.allocEdits.set(code, { ...rest, active: true });
      return this.delay({ saved: code });
    }
    this.allocAdded.push(structuredClone({ ...item, code }));
    return this.delay({ saved: code });
  }

  async allocWhatIf(_siteId: string, change: WhatIf): Promise<EngineResult> {
    return this.delay(structuredClone(solve(withChange(this.allocInput(), change))));
  }

  /* ── build 6: changing a resource on the register ── */
  allocRegister(_siteId: string): Promise<(Resource & { active: boolean })[]> {
    return this.delay(structuredClone(this.allocRegisterAll()));
  }

  /** As the platform's store.change_resource checks it: the register, then the flights placed on it. */
  async allocChangeResource(
    _siteId: string,
    code: string,
    changes: Partial<Resource> & { active?: boolean },
    _note?: string,
  ): Promise<{ resource: Resource & { active: boolean }; changed: string[] }> {
    const bad = (why: string) => new DataSourceError(why, { status: 400 });
    const all = this.allocRegisterAll();
    const row = all.find((r) => r.code === code);
    if (!row) throw new DataSourceError(`${code} is not in the register`, { status: 404 });
    const unknown = Object.keys(changes).filter((k) => !(REGISTER_FIELDS as readonly string[]).includes(k));
    if (unknown.length) throw bad(`can change ${REGISTER_FIELDS.join(", ")}; not ${unknown.join(", ")}`);
    if (!Object.keys(changes).length) throw bad("nothing to change");
    const kinds = new Map(all.filter((r) => r.active).map((r) => [r.code, r.kind]));
    const after: Record<string, unknown> = { ...row };
    const codes = (v: unknown, what: string): string[] | null => {
      if (v === null || v === undefined) return null;
      if (!Array.isArray(v) || !v.every((x) => typeof x === "string")) throw bad(`${what} is a list of codes`);
      const out = [...new Set(v.map((x) => x.trim()).filter(Boolean))];
      return out.length ? out : null;
    };
    for (const [k, raw] of Object.entries(changes) as [string, unknown][]) {
      let v: unknown = raw;
      if (k === "name" || k === "group") {
        v = raw == null ? null : String(raw).trim() || null;
        const max = k === "name" ? 80 : 40;
        if (typeof v === "string" && v.length > max) throw bad(`a ${k} is at most ${max} characters`);
      } else if (k === "seq") {
        if (raw === null || raw === undefined || raw === "") v = null;
        else if (typeof raw !== "number" || !Number.isInteger(raw) || raw < 0 || raw > 9999)
          throw bad("the order is a whole number from 0 to 9999");
      } else if (k === "maxSize") {
        v = raw == null ? null : String(raw).trim().toUpperCase() || null;
        if (v && !["A", "B", "C", "D", "E", "F"].includes(v as string))
          throw bad("the largest size is a code letter, A, B, C, D, E, F");
      } else if (k === "servedInPlace" || k === "bus" || k === "active") {
        if (typeof raw !== "boolean") throw bad(`${k} is yes or no`);
        if (k === "servedInPlace" && raw && row.kind !== "position") throw bad("only a stand is a contact stand");
        if (k === "bus" && raw && row.kind !== "gate") throw bad("only a gate is a bus gate");
      } else if (k === "uses") {
        v = codes(raw, "uses");
        const wrong = ((v as string[] | null) ?? []).filter((u) => !(REGISTER_USES as readonly string[]).includes(u));
        if (wrong.length) throw bad(`${wrong.join(", ")} is not a use; the uses are ${REGISTER_USES.join(", ")}`);
      } else if (k === "excludes") {
        v = codes(raw, "the neighbors");
        for (const x of (v as string[] | null) ?? []) {
          if (x === code) throw bad(`${code} can't exclude itself`);
          if (kinds.get(x) !== row.kind) throw bad(`${x} is not a ${KIND_ONE[row.kind]} in the register`);
        }
      } else if (k === "links") {
        v = codes(raw, "the gates");
        if (v && row.kind !== "position") throw bad("only a stand has its own gate");
        for (const x of (v as string[] | null) ?? [])
          if (kinds.get(x) !== "gate") throw bad(`gate ${x} is not in the register; add it first`);
      }
      after[k] = v;
    }
    const before = row as unknown as Record<string, unknown>;
    const changed = REGISTER_FIELDS.filter(
      (k) => k in changes && JSON.stringify(before[k] ?? null) !== JSON.stringify(after[k] ?? null),
    );
    if (!changed.length) return this.delay({ resource: structuredClone(row), changed: [] });
    const inp = this.allocInput();
    const now = Date.now();
    const onIt = inp.current.filter((a) => a.segments.some((sg) => sg.codes.includes(code) && Date.parse(sg.to) >= now));
    const labels = new Map(inp.demands.map((d) => [d.key, d.label]));
    if (onIt.length && after.active === false) {
      const names = onIt.slice(0, 4).map((a) => labels.get(a.key) ?? "a flight").join(", ") + (onIt.length > 4 ? " …" : "");
      throw bad(`${code} has ${onIt.length} placed on it (${names}); move them before taking it off the register`);
    }
    if (onIt.length && changed.some((k) => ["maxSize", "uses", "links", "servedInPlace", "bus"].includes(k))) {
      const base = inp.resources.find((r) => r.code === code) ?? row;
      const trial: Record<string, unknown> = { ...base };
      for (const k of changed) {
        if (k === "active") continue;
        if (after[k] === null || after[k] === undefined) delete trial[k];
        else trial[k] = after[k];
      }
      const res = inp.resources.map((r) => (r.code === code ? (trial as unknown as Resource) : r));
      const why: string[] = [];
      for (const a of onIt) {
        const cs = a.segments.find((sg) => sg.codes.includes(code))?.codes ?? [code];
        for (const r of checkPlacement({ ...inp, resources: res }, a.key, cs))
          if (r.code === code && ["too_big", "use", "link"].includes(r.kind) && !why.includes(r.why)) why.push(r.why);
      }
      if (why.length) throw bad(`after this change: ${why.slice(0, 3).join(" ")} Move those first, or keep it as it is.`);
    }
    const edit = { ...(this.allocEdits.get(code) ?? {}) } as Record<string, unknown>;
    for (const k of changed) edit[k] = after[k];
    this.allocEdits.set(code, edit as Partial<Resource> & { active?: boolean });
    for (const pr of this.alloc?.proposals ?? []) if (pr.status === "pending") pr.status = "expired";
    return this.delay({ resource: structuredClone(this.allocRegisterAll().find((r) => r.code === code)!), changed });
  }

  /* ── build 2: inspections ── */
  /* rounds as the platform would hold them: the sample's completed rounds, then what is filed here */
  private rounds = seedRounds();
  private roundKeys = new Map<string, { body: string; result: InspectionRoundResult }>();

  private roundResult(r: InspectionRound, created: boolean, replayed: boolean): InspectionRoundResult {
    return {
      id: r.id,
      reference: r.reference,
      status: r.status,
      created,
      replayed,
      completedAt: r.completedAt,
      score: r.score,
      answers: r.answers.length,
      failed: r.answers.filter((a) => a.result === "fail").length,
      skipped: r.answers.filter((a) => a.result === "na").length,
      missingEvidence: Array.isArray(r.attrs.missing_evidence) ? (r.attrs.missing_evidence as string[]) : [],
      warnings: [],
      workOrders: r.workOrders,
    };
  }

  /** What the platform does with a whole round (work/rounds.py), refusals and all. */
  async recordInspectionRound(input: InspectionRoundInput, idempotencyKey?: string): Promise<InspectionRoundResult> {
    const body = JSON.stringify(input);
    const held = idempotencyKey ? this.roundKeys.get(idempotencyKey) : undefined;
    if (held) {
      if (held.body !== body)
        throw new DataSourceError(
          "this idempotency key was already used with a different payload; generate a new key rather than reusing one",
          { status: 409 },
        );
      return this.delay({ ...held.result, replayed: true });
    }
    const list = SAMPLE_CHECKLISTS[input.template];
    if (!list)
      throw new DataSourceError(`checklist '${input.template}' is not available to this tenant`, { status: 400 });
    if (input.work.length && !input.complete)
      throw new DataSourceError("work is raised when the round is completed", { status: 422 });
    const prior = this.rounds.find((r) => r.clientId === input.clientId);
    if (prior) return this.delay(this.roundResult(prior, false, true));
    const seen = new Set<string>();
    for (const a of input.answers) {
      const why = (a.skippedReason ?? "").trim();
      if (!list.items.includes(a.itemKey))
        throw new DataSourceError(`'${a.itemKey}' is not an item of checklist '${input.template}'`, { status: 422 });
      if (!list.options.includes(a.value))
        throw new DataSourceError(
          `'${a.value}' is not an answer to '${a.itemKey}'; answer one of: ${list.options.join(", ")}`,
          { status: 422 },
        );
      if (a.value === "skip" && !why) throw new DataSourceError(`say why '${a.itemKey}' was skipped`, { status: 422 });
      if (why && a.value !== "skip")
        throw new DataSourceError(`'${a.itemKey}' is answered and skipped at once; send one or the other`, {
          status: 422,
        });
      if (seen.has(a.clientId))
        throw new DataSourceError(`two answers share the client id '${a.clientId}'`, { status: 422 });
      seen.add(a.clientId);
    }
    const findings = input.answers.filter((a) => a.value === list.fails);
    const missing = list.photoOnFail ? findings.map((a) => a.itemKey) : [];
    if (input.complete && missing.length && !input.allowMissingEvidence)
      throw new DataSourceError(
        `cannot complete: the pack requires photographic evidence for ${missing.slice(0, 5).join(", ")}`,
        { status: 409 },
      );
    for (const w of input.work) {
      const a = input.answers.find((x) => x.clientId === w.observation);
      if (!a)
        throw new DataSourceError(`work names an answer that is not in this round: '${w.observation}'`, {
          status: 422,
        });
      if (a.value !== list.fails)
        throw new DataSourceError(`work is raised for a finding, and '${a.itemKey}' is not one`, { status: 422 });
    }
    const n = this.rounds.length + 1;
    const id = `00000000-0000-4000-9000-${String(n).padStart(12, "0")}`;
    const reference = roundReference(`R${String(n).padStart(7, "0")}`);
    const now = new Date().toISOString();
    const answers: RoundAnswer[] = input.answers.map((a, i) => ({
      id: `${id}-a${i}`,
      clientId: a.clientId,
      itemKey: a.itemKey,
      result: a.value === "skip" ? "na" : a.value === list.fails ? "fail" : "pass",
      valueText: a.value,
      notes: a.notes,
      skippedReason: a.skippedReason,
      attrs: a.attrs ?? {},
      photos: 0,
    }));
    const workOrders: RoundWork[] = [];
    for (const w of input.work) {
      const a = answers.find((x) => x.clientId === w.observation)!;
      const wo = await this.createWorkOrder({
        siteId: input.siteId,
        title: w.title,
        kind: "corrective",
        priority: Math.min(5, Math.max(1, w.priority)) as WorkOrderPriority,
        description: `${(w.description ?? "").trim()}\n\nFrom inspection round ${reference}.`.trim(),
        slaHours: w.slaHours,
        area: w.area,
        trade: w.trade,
      });
      workOrders.push({
        id: wo.id,
        number: wo.number,
        status: wo.status,
        priority: wo.priority,
        observationId: a.id,
        observation: a.clientId,
        itemKey: a.itemKey,
      });
    }
    const scored = answers.filter((a) => a.result === "pass" || a.result === "fail");
    const round = {
      siteId: input.siteId,
      id,
      reference,
      clientId: input.clientId,
      template: input.template,
      templateName: list.name,
      status: input.complete ? "completed" : "in_progress",
      startedAt: now,
      completedAt: input.complete ? now : undefined,
      score: scored.length
        ? Math.round((10000 * scored.filter((a) => a.result === "pass").length) / scored.length) / 100
        : undefined,
      inspector: this.principal,
      capturedOffline: false,
      attrs: { ...(input.attrs ?? {}), ...(missing.length && input.complete ? { missing_evidence: missing } : {}) },
      answers,
      workOrders,
    };
    this.rounds.unshift(round);
    const result = this.roundResult(round, true, false);
    if (missing.length && input.complete)
      result.warnings = [`completed with ${missing.length} item(s) missing required evidence`];
    if (idempotencyKey) this.roundKeys.set(idempotencyKey, { body, result });
    return this.delay(result);
  }

  listInspectionRounds(q: InspectionRoundQuery): Promise<InspectionRound[]> {
    return this.delay(
      this.rounds
        .filter((r) => r.siteId === q.siteId && r.status === "completed")
        .filter((r) => !q.templates.length || q.templates.includes(r.template))
        .slice(0, q.limit ?? 100)
        .map(({ siteId: _s, ...r }) => r),
    );
  }

  async allocWindows(_siteId: string, code: string, minutes: number, hours = 24): Promise<Window[]> {
    const input = this.allocInput();
    const from = new Date(Math.ceil(input.now.getTime() / 1_800_000) * 1_800_000);
    const to = new Date(from.getTime() + hours * 3_600_000);
    return this.delay(bestWindows(input, code, minutes, { from: from.toISOString(), to: to.toISOString() }, 30).slice(0, 8));
  }

  async allocSetPolicy(_siteId: string, policy: Policy): Promise<Policy[]> {
    this.allocInput();
    this.alloc!.policies[policy.kind] = { ...policy };
    return this.delay(structuredClone(Object.values(this.alloc!.policies)));
  }

  async allocSaveRule(_siteId: string, rule: AllocRule): Promise<AllocRule[]> {
    this.allocInput();
    const a = this.alloc!;
    const id = rule.id || `rule-${Date.now()}`;
    a.rules = [...a.rules.filter((r) => r.id !== id), { ...rule, id }];
    return this.delay(structuredClone(a.rules));
  }

  /* ── passenger flow ─────────────────────────────────────────────────── */
  private flow: {
    checkpoints: Checkpoint[];
    plans: LanePlan[];
    observations: Observation[];
    log: FlowDay["log"];
    domestic: string[];
  } | null = null;

  private flowInput(now = new Date()) {
    this.allocTick();
    const input = this.allocInput(now);
    const flights = flightsFrom(this.aodbState(), this.alloc!.plan, input.resources, new Set(this.flow?.domestic ?? DOMESTIC_NODES), now);
    if (!this.flow) {
      const checkpoints = mockCheckpoints();
      const plans = mockPlans(now);
      /* the sensor reads a little over what the model expects at domestic security, as real ones do */
      const first = forecast({ flights, checkpoints, plans, observations: [], now });
      const at = now.getTime() - 4 * 60_000;
      /* the model's queue just before the sensor's count, as the nowcast compares them */
      const before = first.checkpoints.find((c) => c.code === "SEC-D")?.slots.filter((x) => Date.parse(x.at) <= at - 5 * 60_000).at(-1);
      this.flow = { checkpoints, plans, observations: mockObservations(now, before?.queue ?? 20), log: [], domestic: [...DOMESTIC_NODES] };
    }
    return { flights, checkpoints: this.flow.checkpoints, plans: this.flow.plans, observations: this.flow.observations, now };
  }

  flowDay(_siteId: string): Promise<FlowDay> {
    const inp = this.flowInput();
    const f = this.flow!;
    return this.delay(
      structuredClone({
        forecast: forecast(inp),
        checkpoints: f.checkpoints,
        plans: f.plans,
        observations: f.observations,
        log: f.log,
        domesticNodes: f.domestic,
        sample: true,
      }),
    );
  }

  async flowObserve(_siteId: string, obs: Omit<Observation, "source" | "by">): Promise<{ ok: true }> {
    this.flowInput();
    const f = this.flow!;
    if (!f.checkpoints.some((c) => c.code === obs.code)) throw new DataSourceError("no such checkpoint", { status: 404 });
    if (obs.queue == null && obs.waitMin == null) throw new DataSourceError("a count is a queue length or a wait", { status: 400 });
    f.observations = [{ ...obs, source: "person" as const, by: this.principal }, ...f.observations].slice(0, 200);
    f.log.unshift({ at: new Date().toISOString(), code: obs.code, what: obs.queue != null ? `counted ${obs.queue} in the queue` : `timed a ${obs.waitMin}-minute wait`, by: this.principal });
    return this.delay({ ok: true as const });
  }

  async flowAdopt(_siteId: string, code: string, steps: LanePlan["steps"], note?: string): Promise<LanePlan[]> {
    this.flowInput();
    const f = this.flow!;
    if (!steps.length) throw new DataSourceError("a plan has at least one step", { status: 400 });
    const plan: LanePlan = { code, steps, by: this.principal, at: new Date().toISOString() };
    f.plans = [...f.plans.filter((p) => p.code !== code), plan];
    f.log.unshift({ at: plan.at!, code, what: note || `adopted the recommended plan: ${steps.length} steps`, by: this.principal });
    return this.delay(structuredClone(f.plans));
  }

  async flowSaveCheckpoint(_siteId: string, cp: Checkpoint): Promise<Checkpoint[]> {
    this.flowInput();
    const f = this.flow!;
    f.checkpoints = [...f.checkpoints.filter((c) => c.code !== cp.code), cp];
    f.log.unshift({ at: new Date().toISOString(), code: cp.code, what: "checkpoint settings changed", by: this.principal });
    return this.delay(structuredClone(f.checkpoints));
  }

  /* ── the finance system ─────────────────────────────────────────────────
   *
   * The demonstration has no platform to hold a connection and makes no call
   * to SAP, and says so rather than pretending: the settings panel keeps what
   * is typed for this session, a test answers "not called", and "read now"
   * is refused. A journal file IS read — by the same readSap the platform's
   * read agrees with — so the file path works in the demonstration as it
   * does on the platform. */

  private finance: { connection: FinanceConnection | null; latest: FinanceLatest; runs: FinanceRun[] } = {
    connection: null,
    latest: { read: null, run: null },
    runs: [],
  };

  async financeConnection(_siteId: string): Promise<FinanceConnection | null> {
    return this.delay(this.finance.connection);
  }

  async saveFinanceConnection(siteId: string, input: FinanceConnectionInput): Promise<FinanceConnection> {
    const before = this.finance.connection;
    if (input.enabled && input.companyCodes.length === 0)
      throw new DataSourceError("an enabled connection names at least one company code — the journal is never read whole", {
        status: 400,
      });
    if (input.secret)
      throw new DataSourceError(
        "the demonstration holds no key to keep an entered secret; name an environment secret instead",
        { status: 400 },
      );
    const secret =
      input.clearSecret || (!input.secretRef && !before)
        ? { set: false, source: null, name: null, setAt: null }
        : input.secretRef
          ? { set: false, source: "environment" as const, name: input.secretRef, setAt: new Date().toISOString() }
          : before!.secret;
    this.finance.connection = {
      siteId,
      enabled: input.enabled ?? before?.enabled ?? false,
      label: input.label ?? null,
      baseUrl: input.baseUrl,
      servicePath: input.servicePath || "/sap/opu/odata/sap/API_OPLACCTGDOCITEMCUBE_SRV/A_OperationalAcctgDocItemCube",
      planPath: input.planPath ?? null,
      authMode: input.authMode,
      username: input.username ?? null,
      clientId: input.clientId ?? null,
      tokenUrl: input.tokenUrl ?? null,
      scope: input.scope ?? null,
      sapClient: input.sapClient ?? null,
      companyCodes: input.companyCodes,
      currency: input.currency ?? null,
      accountRules: input.accountRules ?? before?.accountRules ?? [],
      fiscalStartMonth: input.fiscalStartMonth ?? 1,
      fiscalNamedFor: input.fiscalNamedFor ?? "start",
      scheduleMinutes: input.scheduleMinutes ?? null,
      secret,
      canStoreSecret: false,
      lastAttemptAt: before?.lastAttemptAt ?? null,
      lastSuccessAt: before?.lastSuccessAt ?? null,
      lastStatus: before?.lastStatus ?? "never",
      lastError: null,
      lastErrorKind: null,
      updatedAt: new Date().toISOString(),
      service: "API_OPLACCTGDOCITEMCUBE_SRV",
    };
    return this.delay(this.finance.connection);
  }

  async testFinanceConnection(_siteId: string): Promise<FinanceTest> {
    return this.delay({
      ok: false,
      service: "API_OPLACCTGDOCITEMCUBE_SRV",
      kind: "not_configured" as const,
      says: "The demonstration makes no call to SAP. On the platform this reaches SAP, signs in and reads one journal line.",
    });
  }

  async readFinanceNow(): Promise<{ id: string; status: string; fiscalYear: string; fiscalPeriod: string }> {
    throw new DataSourceError("the demonstration makes no call to SAP; read now works on the platform", { status: 400 });
  }

  async financeStatus(_siteId: string): Promise<FinanceStatus> {
    const c = this.finance.connection;
    const lastRun = this.finance.runs[0] ?? null;
    return this.delay({
      configured: c !== null,
      enabled: c?.enabled ?? false,
      label: c?.label ?? null,
      companyCodes: c?.companyCodes ?? [],
      scheduleMinutes: c?.scheduleMinutes ?? null,
      lastAttemptAt: null,
      lastSuccessAt: null,
      lastStatus: "never" as const,
      lastError: null,
      lastErrorKind: null,
      says: null,
      running: false,
      lastRun,
      lastJournalRun: this.finance.runs.find((r) => r.kind === "journal" && r.status === "ok") ?? null,
      service: "API_OPLACCTGDOCITEMCUBE_SRV",
    });
  }

  async financeLatest(_siteId: string): Promise<FinanceLatest> {
    return this.delay(this.finance.latest);
  }

  async importFinanceFile(_siteId: string, fileName: string, text: string): Promise<FinanceImportResult> {
    const lines = readJournalCsv(text);
    if (lines === null)
      throw new DataSourceError(
        "the demonstration reads a journal extract in SAP's field names here; the monthly management pack is imported on the executive desks",
        { status: 400 },
      );
    if (lines.length === 0) throw new DataSourceError("the file has the journal's columns and no lines", { status: 400 });
    const rules = [...(this.finance.connection?.accountRules ?? []), ...ACCOUNT_RULES];
    const at = new Date().toISOString();
    const read = readSap(
      {
        system: `file · ${fileName}`,
        service: "API_OPLACCTGDOCITEMCUBE_SRV",
        readAt: at,
        period: {
          companyCode: String(lines[0].CompanyCode ?? ""),
          fiscalYear: String(lines[0].FiscalYear ?? ""),
          fiscalPeriod: String(lines[0].FiscalPeriod ?? ""),
          state: "unknown",
        },
        lines,
      },
      rules,
    );
    const report = {
      lines: read.lines,
      mapped: read.mapped,
      rejected: read.rejected,
      unmapped: read.unmapped,
      planned: read.planned,
      unbudgeted: read.unbudgeted,
      currency: read.currency,
      file: fileName,
    };
    const run: FinanceRun = {
      id: `demo-${this.finance.runs.length + 1}`,
      source: "file",
      kind: "journal",
      trigger: "file",
      status: "ok",
      fiscalYear: read.period.fiscalYear,
      fiscalPeriod: read.period.fiscalPeriod,
      fileName,
      startedAt: at,
      finishedAt: at,
      report,
      error: null,
      errorKind: null,
      says: null,
    };
    this.finance.runs.unshift(run);
    this.finance.latest = { read: { ...read, rules }, run };
    return this.delay({ id: run.id, status: "ok" as const, kind: "journal" as const, report });
  }

  async allocDeleteRule(_siteId: string, id: string): Promise<AllocRule[]> {
    this.allocInput();
    this.alloc!.rules = this.alloc!.rules.filter((r) => r.id !== id);
    return this.delay(structuredClone(this.alloc!.rules));
  }

  /* ── staff calls ──────────────────────────────────────────────────── */

  callsIce(): Promise<IceConfig> {
    return this.delay(this.staffCalls.ice());
  }

  callsDirectory(q?: string): Promise<CallDirectory> {
    return this.delay(this.staffCalls.directory(q));
  }

  /* the poll is the one call made every second and a half; it pays no latency */
  async callsPoll(_after: number, afterMsg?: number): Promise<CallPoll> {
    return this.staffCalls.poll(afterMsg);
  }

  async callsCreate(
    invitees: string[],
    media: CallMedia,
    opts?: CallStartOptions,
  ): Promise<{ call: Call; notes: string[] }> {
    return this.delay(this.staffCalls.create(invitees, media, opts));
  }

  async callsAnswer(callId: string, video: boolean): Promise<Call> {
    return this.delay(this.staffCalls.answer(callId, video));
  }

  async callsDecline(callId: string): Promise<Call> {
    return this.delay(this.staffCalls.decline(callId));
  }

  async callsLeave(callId: string): Promise<Call> {
    return this.delay(this.staffCalls.leave(callId));
  }

  async callsInvite(callId: string, invitees: string[]): Promise<Call> {
    return this.delay(this.staffCalls.invite(callId, invitees));
  }

  /* nobody is on the other end of a sample call, so there is nobody to signal */
  async callsSignal(): Promise<void> {}

  async callsMedia(callId: string, audioOn: boolean, videoOn: boolean, sharing?: boolean): Promise<Call> {
    return this.delay(this.staffCalls.media(callId, audioOn, videoOn, sharing));
  }

  /* staff calls v2 on the sample: the same rules, the sample's people */
  async callsDnd(): Promise<DndState> {
    return this.delay(this.staffCalls.dndState());
  }

  async callsSetDnd(until: string | "forever" | null, allow: string[]): Promise<DndState> {
    return this.delay(this.staffCalls.setDnd(until, allow));
  }

  async callsJoin(callId: string): Promise<{ call: Call; state: "joined" | "waiting" }> {
    return this.delay(this.staffCalls.join(callId));
  }

  /* ── build 2: safety ── */
  /* The sample's safety register (mock/safety.ts): seeded from the board's
     own sample, worked by the platform's rules, kept for the session. */
  private safety = new MockSafety(this.principal);

  private safe<T>(work: () => T): Promise<T> {
    try {
      return this.delay(work());
    } catch (e) {
      return Promise.reject(e);
    }
  }

  async safetyVocabulary(): Promise<SafetyVocabulary> {
    return this.safe(() => this.safety.vocabulary());
  }

  async listHazards(siteId: string): Promise<HazardPage> {
    return this.safe(() => this.safety.listHazards(siteId));
  }

  async getHazard(id: string): Promise<HazardDetail> {
    return this.safe(() => this.safety.getHazard(id));
  }

  async raiseHazard(input: RaiseHazardInput, key?: string): Promise<{ id: string; reference: string }> {
    return this.safe(() => this.safety.raiseHazard(input, key));
  }

  async assessHazard(id: string, input: AssessHazardInput): Promise<AssessedHazard> {
    return this.safe(() => this.safety.assessHazard(id, input));
  }

  async addHazardAction(id: string, input: HazardActionInput): Promise<{ seq: number; status: string; warnings: string[] }> {
    return this.safe(() => this.safety.addHazardAction(id, input));
  }

  async completeHazardAction(
    id: string,
    seq: number,
    status: "done" | "cancelled",
  ): Promise<{ hazardStatus: string; warnings: string[] }> {
    return this.safe(() => this.safety.completeHazardAction(id, seq, status));
  }

  async acceptHazardRisk(id: string, input: { rationale: string; reviewDue?: string }): Promise<{ note: string }> {
    return this.safe(() => this.safety.acceptHazardRisk(id, input));
  }

  async closeHazard(id: string, input: CloseHazardInput): Promise<{ status: string; warnings: string[] }> {
    return this.safe(() => this.safety.closeHazard(id, input));
  }

  async listSafetyReports(siteId: string): Promise<SafetyReportPage> {
    return this.safe(() => this.safety.listReports(siteId));
  }

  async getSafetyReport(id: string): Promise<SafetyReport> {
    return this.safe(() => this.safety.getReport(id));
  }

  async fileSafetyReport(input: FileReportInput, key?: string): Promise<FiledReport> {
    return this.safe(() => this.safety.fileReport(input, key));
  }

  async setSafetyReportStatus(
    id: string,
    input: { status: ReportStatus; outcome?: string },
  ): Promise<{ reference: string; status: ReportStatus }> {
    return this.safe(() => this.safety.setReportStatus(id, input));
  }

  async linkSafetyReport(id: string, hazardId: string | null): Promise<{ reference: string; hazardReference: string | null }> {
    return this.safe(() => this.safety.linkReport(id, hazardId));
  }

  async raiseHazardFromReport(
    id: string,
    input: { category: string; title: string; description?: string },
  ): Promise<{ id: string; reference: string; warning?: string }> {
    return this.safe(() => this.safety.raiseFromReport(id, input));
  }

  async securityVocabulary(): Promise<IncidentCategory[]> {
    return this.safe(() => this.safety.incidentCategories());
  }

  async listSecurityIncidents(siteId: string): Promise<SecurityIncidentPage> {
    return this.safe(() => this.safety.listIncidents(siteId));
  }

  async raiseSecurityIncident(input: RaiseIncidentInput, key?: string): Promise<RaisedIncident> {
    return this.safe(() => this.safety.raiseIncident(input, key));
  }

  async setSecurityIncidentStatus(
    id: string,
    input: { status: IncidentStatus; outcome?: string; note?: string },
  ): Promise<{ status: IncidentStatus; warnings: string[] }> {
    return this.safe(() => this.safety.setIncidentStatus(id, input));
  }

  async linkSecurityIncident(id: string, input: { subjectKind: string; subjectId: string; note?: string }): Promise<void> {
    return this.safe(() => this.safety.linkIncident(id, input));
  }

  async callsSettings(callId: string, patch: CallSettingsPatch): Promise<Call> {
    return this.delay(this.staffCalls.settings(callId, patch));
  }

  async callsAdmit(callId: string, principalId: string, allow: boolean): Promise<Call> {
    return this.delay(this.staffCalls.admit(callId, principalId, allow));
  }

  async callsRemove(callId: string, principalId: string): Promise<Call | undefined> {
    return this.delay(this.staffCalls.remove(callId, principalId));
  }

  async callsMute(callId: string, who: string | "all"): Promise<Call | undefined> {
    return this.delay(this.staffCalls.mute(callId, who));
  }

  async callsRole(callId: string, principalId: string, role: ParticipantRole): Promise<Call | undefined> {
    return this.delay(this.staffCalls.role(callId, principalId, role));
  }

  async callsEnd(callId: string): Promise<Call | undefined> {
    return this.delay(this.staffCalls.end(callId));
  }

  async callsHand(callId: string, up: boolean): Promise<Call> {
    return this.delay(this.staffCalls.hand(callId, up));
  }

  async callsMessages(callId: string, after?: number): Promise<CallMessage[]> {
    return this.delay(this.staffCalls.messagesFor(callId, after));
  }

  async callsSendMessage(callId: string, text: string): Promise<CallMessage> {
    return this.delay(this.staffCalls.sendMessage(callId, text));
  }

  async callsLive(): Promise<LiveCall[]> {
    return this.delay(this.staffCalls.live());
  }

  async callsMeetings(scope: "upcoming" | "past"): Promise<Meeting[]> {
    return this.delay(this.staffCalls.meetingList(scope));
  }

  async callsSaveMeeting(draft: MeetingDraft, id?: string): Promise<Meeting> {
    return this.delay(this.staffCalls.saveMeeting(draft, id));
  }

  async callsCancelMeeting(id: string): Promise<Meeting> {
    return this.delay(this.staffCalls.cancelMeeting(id));
  }

  async callsStartMeeting(id: string): Promise<{ meeting: Meeting; call: Call; notes?: string[] }> {
    return this.delay(this.staffCalls.startMeeting(id));
  }

  callsHistory(limit?: number): Promise<Call[]> {
    return this.delay(this.staffCalls.history(limit));
  }

  /* the sample's people have no photographs; their tiles show initials */
  async callsPhoto(): Promise<string | undefined> {
    return undefined;
  }

  callsPolicy(): Promise<PolicyView> {
    return this.delay(this.staffCalls.policyView());
  }

  async saveCallsPolicy(policy: CallPolicy, basedOn?: string): Promise<PolicyView> {
    return this.delay(this.staffCalls.savePolicy(policy, basedOn, this.principal));
  }

  async checkCallsPolicy(from: string, to: string) {
    const d = this.staffCalls.check(from, to);
    return this.delay({ allowed: d.allowed, video: d.video, reason: d.reason, rule: d.rule });
  }

  async simulateIncomingCall(media: CallMedia): Promise<void> {
    this.staffCalls.simulateIncoming(media);
  }

  getDirectory(_siteId: string): Promise<Directory> {
    return this.delay(
      structuredClone({ orgs: this.directory, people: this.loosePeople, channels: { sms: false, email: false } }),
    );
  }

  async saveContactOrg(_siteId: string, org: Omit<ContactOrg, "people">): Promise<{ id: string }> {
    if (!org.name.trim()) throw new DataSourceError("an organisation needs a name", { status: 400 });
    const found = org.id ? this.directory.find((o) => o.id === org.id) : undefined;
    if (found) Object.assign(found, { ...org, people: found.people });
    else this.directory.push({ ...org, id: `org-${Date.now().toString(36)}`, people: [] });
    return this.delay({ id: found?.id ?? this.directory[this.directory.length - 1].id! });
  }

  async saveContactPerson(person: ContactPerson): Promise<{ id: string }> {
    if (!person.name.trim()) throw new DataSourceError("a contact needs a name", { status: 400 });
    const points = normalisePoints(
      person.points.map((p) => {
        const c = checkPoint(p);
        if (!c.ok) throw new DataSourceError(c.why, { status: 400 });
        return { ...p, value: c.value };
      }),
    );
    const clean: ContactPerson = { ...person, points, id: person.id ?? `person-${Date.now().toString(36)}` };
    for (const o of this.directory) o.people = o.people.filter((p) => p.id !== clean.id);
    this.loosePeople = this.loosePeople.filter((p) => p.id !== clean.id);
    const org = this.directory.find((o) => o.id === clean.orgId);
    if (org) org.people.push(clean);
    else this.loosePeople.push(clean);
    return this.delay({ id: clean.id! });
  }

  listNotifyRules(siteId: string): Promise<NotifyRule[]> {
    return this.delay(structuredClone(this.rulesFor(siteId)));
  }

  async saveNotifyRule(rule: NotifyRule): Promise<NotifyRule> {
    if (!rule.events.length) throw new DataSourceError("a rule needs at least one event", { status: 400 });
    if (!rule.recipients.length) throw new DataSourceError("a rule needs at least one recipient", { status: 400 });
    if (!rule.channels.length) throw new DataSourceError("a rule needs at least one channel", { status: 400 });
    if (rule.escalateAfterMin && !rule.escalateTo.length)
      throw new DataSourceError("an escalation needs someone to escalate to", { status: 400 });
    const list = this.rulesFor(rule.siteId);
    const saved = { ...rule, id: rule.id ?? `rule-${Date.now().toString(36)}` };
    const i = list.findIndex((r) => r.id === saved.id);
    if (i >= 0) list[i] = saved;
    else list.push(saved);
    return this.delay(structuredClone(saved));
  }

  previewRecipients(_siteId: string, recipients: Recipient[]): Promise<Reach[]> {
    return this.delay(this.reachFor(recipients));
  }

  listWorkOrderMessages(workOrderId: string): Promise<WorkOrderMessage[]> {
    const dispatch = (this.dispatchOutbox.get(workOrderId) ?? []).map((n) => ({ ...n, group: undefined }));
    return this.delay([...dispatch, ...(this.ruleMessages.get(workOrderId) ?? [])]);
  }

  listDispatchNotifications(workOrderId: string): Promise<DispatchNotification[]> {
    return this.delay([...(this.dispatchOutbox.get(workOrderId) ?? [])]);
  }

  /* ── Store & Warehouse: the sample register, moved by the steps the platform takes ── */

  /*
   * The stores screens on the sample keep their register as a note, as they
   * always have (features/stores/useStores.ts), and never call these. They are
   * here so the sample answers every method a platform does, and they replay
   * each step with the same functions the screen uses (storesOps.applyOp), so
   * a refusal reads the same from either.
   */
  private stores: StoresRegister | null = null;
  private storesImportedNote: string | null = null;

  private storesHeld(): StoresRegister {
    if (!this.stores) this.stores = mockStores();
    return this.stores;
  }

  storesRegister(_siteId: string): Promise<PlatformStores> {
    return this.delay({ register: structuredClone(this.storesHeld()), held: true, noteWaiting: false });
  }

  async storesApply(_siteId: string, ops: StoresOp[]): Promise<StoresApplied> {
    if (!ops.length) throw new DataSourceError("nothing to do", { status: 400 });
    const r = applyOps(this.storesHeld(), ops, { name: this.principal });
    if (!r.ok) throw new DataSourceError(r.why, { status: 409 });
    this.stores = r.register;
    return this.delay({ results: ops.map((o) => ({ op: o.op })), replayed: false });
  }

  async storesImportNote(siteId: string): Promise<StoresImported> {
    const notes = await this.listNotes(siteId, "estate.stores");
    let found: { id: string; value: StoresRegister } | null = null;
    for (const n of notes) {
      try {
        found = { id: n.id, value: JSON.parse(n.body) as StoresRegister };
        break;
      } catch {
        /* not JSON */
      }
    }
    if (!found) throw new DataSourceError("There is no stores register kept as a note for this site.", { status: 404 });
    const counts = (reg: StoresRegister, replayed: boolean): StoresImported => ({
      items: reg.items.length,
      movements: reg.movements.length,
      balancing: 0,
      reservations: reg.reservations.length,
      requisitions: reg.requisitions.length,
      orders: reg.orders.length,
      replayed,
    });
    if (this.storesImportedNote) {
      if (this.storesImportedNote !== found.id) {
        throw new DataSourceError(
          "This site's register was brought onto the platform from an earlier note, and the platform has been the record since. A newer note is not imported on top of it.",
          { status: 409 },
        );
      }
      return this.delay(counts(this.storesHeld(), true));
    }
    this.stores = structuredClone(found.value);
    this.storesImportedNote = found.id;
    return this.delay(counts(this.stores, false));
  }

  async classifyWorkOrder(id: string, input: ClassifyInput): Promise<WorkOrder> {
    const wo = this.find(id);
    const given = classAttrs(input);
    const rest = Object.fromEntries(
      Object.entries(wo.attrs ?? {}).filter(([k]) => k !== "area" && k !== "trade" && k !== "contractor"),
    );
    const changed = (["area", "trade", "contractor"] as const)
      .filter((k) => (wo.attrs?.[k] ?? undefined) !== given[k])
      .map((k) => `${k} ${given[k] ?? "none"}`);
    wo.attrs = { ...rest, ...given };
    if (changed.length)
      this.history.get(id)?.push({
        id: `${id}-cls-${Date.now()}`,
        workOrderId: id,
        at: new Date().toISOString(),
        by: this.principal,
        from: wo.status,
        to: wo.status,
        note: `Set ${changed.join(", ")}`,
      });
    return this.delay(this.withTransitions(wo));
  }

  async setWorkOrderSla(id: string, input: SetSlaInput): Promise<WorkOrder> {
    const wo = this.find(id);
    if (!input.reason.trim())
      throw new DataSourceError("a reason is required to set or change an SLA", { status: 409 });
    if (!isOpen(wo.status))
      throw new DataSourceError(`${wo.number} is ${wo.status}; an SLA cannot be changed once the outcome is known`, {
        status: 409,
      });
    const before = wo.slaHours;
    wo.slaHours = input.slaHours;
    wo.dueAt = new Date(Date.parse(wo.openedAt) + input.slaHours * 3_600_000).toISOString();
    this.history.get(id)?.push({
      id: `${id}-sla-${Date.now()}`,
      workOrderId: id,
      at: new Date().toISOString(),
      by: this.principal,
      from: wo.status,
      to: wo.status,
      note: `SLA set to ${input.slaHours} h${before ? ` (was ${before} h)` : ""}: ${input.reason.trim()}`,
    });
    return this.delay(this.withTransitions(wo));
  }

  /* ── build 3: work orders from the boards ── */
  /*
   * The boards raise and move work with a key minted once per record and
   * reused on TRY AGAIN. As on the platform: the same client id files one
   * order, and the same move key answers with the first move (the same key
   * with a different move is refused). The transition rules are the
   * platform's own table (domain/workOrders.ts `TRANSITIONS`).
   */
  private woClientIds = new Map<string, string>();
  private woMoveKeys = new Map<string, string>();

  /* ── build 2: closures ── */
  /*
   * The closure register, held the way the platform holds it and refusing
   * what it refuses: no reason, a surface the site does not have, a window
   * that ends before it starts, the same NOTAM twice while it stands, and a
   * surface already fully closed. Seeded with the sample's four ended
   * closures; the map's two sample closures stay the map's own.
   */
  private closures: { c: Closure; started: string | null }[] | null = null;
  private closureKeys = new Map<string, { body: string; answer: unknown }>();
  private closureSeq = 0;

  private heldClosures(): { c: Closure; started: string | null }[] {
    if (!this.closures) this.closures = mockClosures().map((c) => ({ c, started: c.startedAt }));
    return this.closures;
  }

  /** The same key with the same body gives back the first answer; with another body it is refused. */
  private replayed<T>(key: string | undefined, body: unknown): T | undefined {
    if (!key) return undefined;
    const seen = this.closureKeys.get(key);
    if (!seen) return undefined;
    if (seen.body !== JSON.stringify(body))
      throw new DataSourceError(
        "this idempotency key was already used with a different payload; generate a new key rather than reusing one",
        { status: 409 },
      );
    return seen.answer as T;
  }

  async declareClosure(input: DeclareClosureInput, idempotencyKey?: string): Promise<DeclaredClosure> {
    const again = this.replayed<DeclaredClosure>(idempotencyKey, input);
    if (again) return this.delay(again);
    const refuse = (detail: string, status = 409) => {
      throw new DataSourceError(detail, { status });
    };
    const reason = input.reason.trim();
    if (!reason) refuse("a closure needs a reason; an availability figure nobody can explain is a number, not a metric");
    if (!isMockSurface(input.zoneCode)) refuse(`there is no surface ${input.zoneCode} on this site`, 422);
    const planned = input.kind === "planned" && !input.startedAt && !!input.plannedStart;
    const now = new Date().toISOString();
    const started = planned ? null : (input.startedAt ?? now);
    const effective = planned ? input.plannedStart! : started!;
    if (input.plannedEnd && Date.parse(input.plannedEnd) <= Date.parse(effective))
      refuse("a closure must end after it starts");
    const held = this.heldClosures();
    const reference = input.reference.trim();
    const standing = (h: { c: Closure }) => h.c.status === "planned" || h.c.status === "active";
    const dup = reference
      ? held.find((h) => standing(h) && h.c.reference?.trim().toUpperCase() === reference.toUpperCase())
      : undefined;
    if (dup)
      refuse(`${reference} is already recorded and has not ended (it is ${dup.c.status}); lift that one first, or check the number`);
    const closed = !planned && held.find((h) => h.c.status === "active" && h.c.extent === "full" && h.c.zoneCode === input.zoneCode);
    if (closed)
      refuse(
        `this surface is already fully closed (${closed.c.reason}); a second overlapping closure would double-count the lost minutes and report negative availability`,
      );
    const id = `mock-cls-${++this.closureSeq}`;
    held.push({
      started,
      c: {
        id,
        reference: reference || undefined,
        kind: input.kind,
        extent: "full",
        reason,
        status: planned ? "planned" : "active",
        startedAt: effective,
        plannedStart: input.plannedStart,
        plannedEnd: input.plannedEnd ?? undefined,
        zoneCode: input.zoneCode,
        attrs: input.attrs ?? {},
        declaredAt: now,
        declaredBy: this.principal,
      },
    });
    const answer: DeclaredClosure = { id, status: planned ? "planned" : "active", startedAt: started, reference };
    if (idempotencyKey) this.closureKeys.set(idempotencyKey, { body: JSON.stringify(input), answer });
    return this.delay(answer);
  }

  async endClosure(id: string, endedAt?: string, idempotencyKey?: string): Promise<EndedClosure> {
    const again = this.replayed<EndedClosure>(idempotencyKey, { id, endedAt });
    if (again) return this.delay(again);
    const h = this.heldClosures().find((x) => x.c.id === id);
    if (!h) throw new DataSourceError("closure not found", { status: 409 });
    if (h.c.status === "ended" || h.c.status === "cancelled")
      throw new DataSourceError(`this closure is already ${h.c.status}`, { status: 409 });
    const at = endedAt ?? new Date().toISOString();
    h.c.status = "ended";
    h.c.endedAt = at;
    h.c.endedBy = this.principal;
    const minutes = h.started ? Math.max(0, (Date.parse(at) - Date.parse(h.started)) / 60_000) : 0;
    h.c.minutes = Math.round(minutes);
    const answer: EndedClosure = { endedAt: at, minutes: Math.round(minutes * 10) / 10 };
    if (idempotencyKey) this.closureKeys.set(idempotencyKey, { body: JSON.stringify({ id, endedAt }), answer });
    return this.delay(answer);
  }

  async transitionWorkOrder(id: string, input: TransitionInput, idempotencyKey?: string): Promise<WorkOrder> {
    const wo = this.find(id);
    /* build 3: as the platform, a move sent again with its key answers with the first one */
    const body = JSON.stringify({ id, ...input });
    const seen = idempotencyKey ? this.woMoveKeys.get(idempotencyKey) : undefined;
    if (seen !== undefined) {
      if (seen !== body) {
        throw new DataSourceError("this idempotency key was already used with a different payload", { status: 409 });
      }
      return this.delay(this.withTransitions(wo));
    }
    const to = input.to;
    if (!canTransition(wo.status, to)) {
      const allowed = legalTransitions(wo.status).join(", ") || "none — this state is terminal";
      throw new DataSourceError(`cannot move ${wo.number} from ${wo.status} to ${to}; allowed: ${allowed}`, {
        status: 409,
      });
    }
    if (to === "verified") {
      const completedBy = this.history.get(id)?.findLast((e) => e.to === "completed")?.by;
      if (completedBy === this.principal) {
        throw new DataSourceError("the person who completed the work cannot verify it", { status: 409 });
      }
    }
    if (to === "on_hold" && !input.onHoldReason && !input.note) {
      throw new DataSourceError("on_hold requires a reason", { status: 409 });
    }
    const now = new Date().toISOString();
    const from = wo.status;
    wo.status = to;
    if (to === "in_progress") wo.startedAt = wo.startedAt ?? now;
    if (to === "completed") {
      wo.completedAt = now;
      wo.completedBy = this.principal;
    }
    if (to === "verified") {
      wo.verifiedAt = now;
      wo.verifiedBy = this.principal;
    }
    if (to === "cancelled") wo.verifiedAt = now;
    if (to === "dispatched" || to === "in_progress") wo.completedAt = undefined;
    if (input.costActual != null) wo.costActual = input.costActual;
    this.history.get(id)?.push({
      id: `${id}-ev${(this.history.get(id)?.length ?? 0) + 1}`,
      workOrderId: id,
      at: now,
      by: this.principal,
      from,
      to,
      note: input.note ?? input.onHoldReason,
    });
    for (const a of ALERTS) {
      const act = a.resultingActions.find((r) => r.ref === wo.number);
      if (act) act.status = to;
      if (act && a.kind === "predictive" && to === "completed") a.confirmed = true;
      if (act && a.kind === "predictive" && to === "cancelled") a.confirmed = false;
    }
    if ((EVENTS as string[]).includes(to)) this.applyRules(wo, to as NoticeEvent);
    if (to === "completed") this.autoVerify(wo);
    if (idempotencyKey) this.woMoveKeys.set(idempotencyKey, body);
    return this.delay(this.withTransitions(wo));
  }

  listKpis(siteId: string): Promise<Kpi[]> {
    /* build 6B: the sample's targets are the pack's; a site's own target, once set here, wins */
    return this.delay(
      seedKpis(siteId).map((k) => {
        const own = this.kpiTargets.get(`${siteId}|${k.key}`);
        const pack = { packTarget: k.target, targetStatus: k.target != null ? ("pack" as const) : undefined };
        if (!own) return { ...k, ...pack };
        return {
          ...k,
          ...pack,
          target: own.target,
          targetStatus: own.status,
          targetBasis: own.basis,
          targetSetBy: own.by,
          targetSetAt: own.at,
          targetScope: "site" as const,
        };
      }),
    );
  }

  /* ── build 6B: entry forms ── */
  private kpiTargets = new Map<string, { target: number; status: TargetStatus; basis: string; by: string; at: string }>();
  private kpiTargetLog = new Map<string, KpiTargetChange[]>();

  private refused(detail: string, status = 400): DataSourceError {
    const prefix = status === 404 ? "Not found" : status === 422 || status === 409 ? "" : String(status);
    return new DataSourceError(prefix ? `${prefix}: ${detail}` : detail, { status, detail });
  }

  async setKpiTarget(siteId: string, input: KpiTargetInput): Promise<void> {
    const why = kpiTargetRefusal(
      input,
      seedKpis(siteId).map((k) => k.key),
    );
    if (why) throw this.refused(why);
    const key = `${siteId}|${input.metricKey}`;
    const at = new Date().toISOString();
    this.kpiTargets.set(key, { target: input.target, status: input.status, basis: input.basis.trim(), by: this.principal, at });
    this.kpiTargetLog.set(key, [
      {
        action: "set",
        target: input.target,
        status: input.status,
        basis: input.basis.trim(),
        reason: null,
        scope: "site",
        by: this.principal,
        at,
      },
      ...(this.kpiTargetLog.get(key) ?? []),
    ]);
    await this.delay(null);
  }

  async retireKpiTarget(siteId: string, metricKey: string, reason: string): Promise<void> {
    const why = retireRefusal(reason);
    if (why) throw this.refused(why);
    const key = `${siteId}|${metricKey}`;
    const own = this.kpiTargets.get(key);
    if (!own) throw this.refused(NO_SITE_TARGET, 404);
    this.kpiTargets.delete(key);
    this.kpiTargetLog.set(key, [
      {
        action: "retired",
        target: null,
        status: null,
        basis: null,
        reason: reason.trim(),
        scope: "site",
        by: this.principal,
        at: new Date().toISOString(),
      },
      ...(this.kpiTargetLog.get(key) ?? []),
    ]);
    await this.delay(null);
  }

  kpiTargetHistory(siteId: string, metricKey: string): Promise<KpiTargetChange[]> {
    return this.delay([...(this.kpiTargetLog.get(`${siteId}|${metricKey}`) ?? [])]);
  }

  async amRecordCondition(assetId: string, input: AmConditionInput): Promise<AmConditionResult> {
    return this.delay(this.am.recordCondition(assetId, input, this.principal));
  }

  listEnvelopes(): Promise<FundingEnvelope[]> {
    return this.delay(this.am.listEnvelopes());
  }

  async setEnvelope(_siteId: string, input: EnvelopeInput): Promise<void> {
    this.am.setEnvelope(input, this.principal);
    await this.delay(null);
  }

  async removeEnvelope(id: string): Promise<void> {
    this.am.removeEnvelope(id);
    await this.delay(null);
  }

  async setTestThresholds(_siteId: string, key: string, input: ThresholdsInput): Promise<void> {
    const ty = mockTestTypes().find((x) => x.key === key);
    if (!ty) throw this.refused(`no test type '${key}' is declared for this tenant`, 404);
    const why = thresholdsRefusal(input);
    if (why) throw this.refused(why);
    const basisNote = input.basis.trim();
    setMockThresholds(key, {
      ...(input.thresholds
        ? {
            thresholds: input.thresholds.map((w) => ({
              label: String(w.label ?? ""),
              match: (w.match as Record<string, string> | null) ?? null,
              attention: typeof w.attention === "number" ? w.attention : null,
              fail: typeof w.fail === "number" ? w.fail : null,
              basis: w.basis === "pct_of_specified" ? ("pct_of_specified" as const) : ("value" as const),
              higherIsBetter: w.higher_is_better !== false,
              status: input.status,
              basisNote,
            })),
          }
        : {}),
      ...(input.intervalDays !== undefined ? { intervalDays: input.intervalDays, intervalStatus: input.status } : {}),
      updatedAt: new Date().toISOString(),
      updatedBy: this.principal,
    });
    await this.delay(null);
  }

  listAlerts(siteId: string, kind?: AlertKind): Promise<Alert[]> {
    return this.delay(seedAlerts(siteId).filter((a) => !kind || a.kind === kind));
  }

  async explainAlert(id: string): Promise<AlertExplanation> {
    const a = ALERTS.find((x) => x.id === id);
    if (!a) throw new DataSourceError("alert not found", { status: 404 });
    return this.delay(a);
  }

  listInspections(q: InspectionQuery): Promise<Inspection[]> {
    const rows = INSPECTIONS.filter((i) => i.siteId === q.siteId)
      .filter((i) => !q.status || i.status === q.status)
      .filter((i) => !q.template || i.template === q.template)
      .map(({ observations: _o, attrs: _a, route: _r, scoring: _s, ...row }) => row);
    return this.delay(rows);
  }

  async getInspection(id: string): Promise<InspectionDetail> {
    const i = INSPECTIONS.find((x) => x.id === id);
    if (!i) throw new DataSourceError("inspection not found", { status: 404 });
    return this.delay(i);
  }

  listMapLayers(siteId: string): Promise<MapLayer[]> {
    return this.delay(siteId === "site_oema" ? GEO_LAYERS : []);
  }

  listMapFeatures(siteId: string, layer: string): Promise<MapFeature[]> {
    return this.delay(siteId === "site_oema" ? featuresOf(layer) : []);
  }

  getCompliance(siteId: string): Promise<ComplianceReport> {
    return this.delay(siteId === "site_oema" ? COMPLIANCE : { readiness: [], obligations: [] });
  }

  listRules(): Promise<Rule[]> {
    return this.delay(RULES);
  }

  async verifyRule(input: VerifyRuleInput): Promise<VerifyRuleResult> {
    if (!input.confirmedAgainst.trim()) throw new DataSourceError("confirmed_against is required", { status: 400 });
    const rule = RULES.find((r) => r.key === input.ruleKey);
    if (!rule) throw new DataSourceError("rule not found for this tenant", { status: 404 });
    const now = new Date().toISOString();
    rule.parameters = { ...rule.packDefaults, ...input.parameters };
    rule.enabled = true;
    rule.gatedReason = undefined;
    rule.confirmedAgainst = input.confirmedAgainst;
    rule.verifiedAt = now;
    return this.delay({
      key: rule.key,
      enabled: true,
      parameters: rule.parameters,
      packDefaults: rule.packDefaults,
      confirmedAgainst: input.confirmedAgainst,
      verifiedAt: now,
      note: "stored for this tenant only; the pack's own defaults are unchanged and a pack upgrade cannot revert this",
    });
  }

  listDocuments(q: DocumentQuery): Promise<DocumentRow[]> {
    const needle = q.q?.trim().toLowerCase();
    const rows = documentRows()
      .filter((x) => (q.siteScope === "exclusive" ? x.siteId === q.siteId : x.siteId == null || x.siteId === q.siteId))
      .filter((x) => !q.type || x.type === q.type)
      .filter((x) => !q.category || DOCUMENT_TYPES.find((t) => t.key === x.type)?.category === q.category)
      .filter((x) => !q.status || x.status === q.status)
      .filter((x) => !q.tag || x.tags.includes(q.tag))
      .filter((x) => !needle || [x.title, x.reference, ...x.tags].some((s) => s?.toLowerCase().includes(needle)));
    return this.delay(rows);
  }

  async getDocument(id: string): Promise<DocumentDetail> {
    const doc = DOCUMENTS.find((x) => x.id === id);
    if (!doc) throw new DataSourceError("document not found", { status: 404 });
    return this.delay(doc);
  }

  listDocumentTypes(): Promise<DocumentType[]> {
    return this.delay(DOCUMENT_TYPES);
  }

  whenToWork(q: PlanQuery): Promise<PlanAnswer> {
    return this.delay(mockWhenToWork(q));
  }

  liabilityCurve(_siteId: string, fiscalYear: number): Promise<LiabilityCurve> {
    return this.delay(mockLiabilityCurve(fiscalYear));
  }

  liabilityRegister(): Promise<LiabilityRegister> {
    return this.delay(mockLiabilityRegister());
  }

  /* ---- Asset Management: the sample register, computed like the platform --------- */
  private am = new MockAssetManagement();

  amRegister(_siteId: string, filter?: AmFilter): Promise<AmRegister> {
    return this.delay(this.am.register(filter));
  }
  amAsset(assetId: string): Promise<AmAssetDetail> {
    return this.delay(this.am.asset(assetId));
  }
  amEditAsset(assetId: string, patch: AmPatch): Promise<{ changed: string[] }> {
    return this.delay(this.am.edit(assetId, patch));
  }
  amImport(_siteId: string, rows: AmImportRow[]): Promise<AmImportResult> {
    return this.delay(this.am.import(rows));
  }
  amPolicies(): Promise<AmPolicy[]> {
    return this.delay(this.am.listPolicies());
  }
  amSetPolicy(assetType: string, policy: Omit<AmPolicy, "assetType">): Promise<AmPolicy> {
    return this.delay(this.am.setPolicy(assetType, policy));
  }
  amCondition(): Promise<AmCondition> {
    return this.delay(this.am.condition());
  }
  amRefreshHealth(): Promise<{ assessed: number; changed: number }> {
    return this.delay(this.am.refresh());
  }
  amMaintenance(): Promise<AmMaintenance> {
    return this.delay(this.am.maintenance());
  }
  amCreatePlan(input: AmPlanInput): Promise<AmPlan> {
    return this.delay(this.am.createPlan(input));
  }
  async amEditPlan(
    planId: string,
    patch: { active?: boolean; intervalDays?: number; nextDueOn?: string | null; title?: string },
  ): Promise<void> {
    this.am.editPlan(planId, patch);
    await this.delay(null);
  }
  amApplyPlan(_siteId: string, assetType: string, plan: Omit<AmPlanInput, "assetId">): Promise<{ created: number }> {
    return this.delay(this.am.applyPlan(assetType, plan));
  }
  amGeneratePlans(): Promise<{ created: { planId: string; workOrder: string; asset: string }[] }> {
    return this.delay(this.am.generate());
  }
  amLifecycle(): Promise<AmLifecycle> {
    return this.delay(this.am.lifecycle());
  }
  amTypes(): Promise<AmTypeDef[]> {
    return this.delay(this.am.types());
  }
  amCreateAsset(_siteId: string, input: AmCreateInput): Promise<{ id: string; tag: string }> {
    return this.delay(this.am.create(input));
  }
  amHistory(assetId: string): Promise<AmHistoryEvent[]> {
    return this.delay(this.am.timeline(assetId));
  }
  amNotes(assetId: string): Promise<AmNote[]> {
    return this.delay(this.am.listNotes(assetId));
  }
  async amAddNote(assetId: string, body: string, category: NoteCategory): Promise<void> {
    this.am.addNote(assetId, body, category);
    await this.delay(null);
  }

  /* ---- HR: the sample workforce, priced like the platform ------------------------ */
  private hr = new MockHr(this.am);

  hrPeople(): Promise<HrPeople> {
    return this.delay(this.hr.people());
  }
  async hrAddPerson(_siteId: string, input: HrEmployeeInput): Promise<{ id: string; employeeNo: string }> {
    return this.delay(this.hr.add(input));
  }
  async hrEditPerson(employeeId: string, patch: HrEmployeePatch): Promise<{ changed: string[] }> {
    return this.delay(this.hr.edit(employeeId, patch));
  }
  hrRules(): Promise<HrRulesView> {
    return this.delay(this.hr.rulesView());
  }
  hrSetRules(restated: HrRulesPatch): Promise<HrRulesView> {
    return this.delay(this.hr.setRules(restated));
  }
  hrCost(): Promise<HrCost> {
    return this.delay(this.hr.cost());
  }
  async hrSetOvertime(employeeId: string, month: string, hours: number): Promise<void> {
    this.hr.setOvertime(employeeId, month, hours);
    await this.delay(null);
  }
  hrLabour(): Promise<HrLabour> {
    return this.delay(this.hr.labour());
  }
  hrSaudization(): Promise<HrSaudization> {
    return this.delay(this.hr.saudization());
  }
  async hrFile(employeeId: string): Promise<HrStaffFile> {
    return this.delay(this.hr.staffFile(employeeId));
  }
  async hrSetPhoto(employeeId: string, dataUrl: string): Promise<void> {
    this.hr.setPhoto(employeeId, dataUrl);
    await this.delay(null);
  }
  hrRecords(_siteId: string, filter?: { kind?: string; status?: string }) {
    return this.delay(this.hr.listRecords(filter));
  }
  async hrAddRecord(input: HrRecordInput): Promise<HrRecord> {
    return this.delay(this.hr.addRecord(input));
  }
  async hrDecide(recordId: string, status: RecordStatus, note?: string | null): Promise<HrRecord> {
    return this.delay(this.hr.decide(recordId, status, note));
  }
  hrAttendanceDay(_siteId: string, day: string): Promise<HrAttendanceDay> {
    return this.delay(this.hr.attendanceDay(day));
  }
  hrAttendanceRange(_siteId: string, from: string, to: string, employeeId?: string): Promise<HrAttendanceRow[]> {
    return this.delay(this.hr.attendanceRange(from, to, employeeId));
  }
  hrSetAttendance(input: HrAttendanceInput): Promise<{ recorded: number }> {
    return this.delay(this.hr.setAttendance(input));
  }
  hrAccess(): Promise<HrAccessView> {
    return this.delay(this.hr.accessView());
  }
  async hrSetAccess(principalId: string, role: string | null): Promise<void> {
    this.hr.setAccess(principalId, role);
    await this.delay(null);
  }
  async hrMe(): Promise<HrMe> {
    return this.delay(this.hr.me());
  }
  async hrClockMe(input: HrClockInput) {
    return this.delay(this.hr.clockMe(input));
  }
  async hrSelfRequest(input: HrSelfRequest): Promise<HrRecord> {
    return this.delay(this.hr.selfRequest(input));
  }
  hrGeofences(): Promise<HrGeofence[]> {
    return this.delay(this.hr.geofences());
  }
  async hrAddGeofence(_siteId: string, f: Omit<HrGeofence, "id">): Promise<HrGeofence> {
    return this.delay(this.hr.addGeofence(f));
  }
  async hrEditGeofence(id: string, patch: Partial<HrGeofence>): Promise<void> {
    this.hr.editGeofence(id, patch);
    await this.delay(null);
  }
  hrClockEvents(_siteId: string, day: string, employeeId?: string): Promise<HrClockEvent[]> {
    return this.delay(this.hr.clockDay(day, employeeId));
  }
  async hrClockManual(employeeId: string, direction: "in" | "out", at: string, note: string): Promise<void> {
    this.hr.clockManual(employeeId, direction, at, note);
    await this.delay(null);
  }
  hrNotifications(_siteId: string, filter?: { employeeId?: string; status?: string }) {
    return this.delay(this.hr.notifications(filter));
  }
  hrSendNow() {
    return this.delay(this.hr.sendNow());
  }
  async hrImport(
    _siteId: string,
    rows: { row: number; data: Record<string, unknown>; loginEmail: string | null; leaveBalance: number | null }[],
    opts: { dryRun: boolean; updateExisting: boolean },
  ): Promise<ImportResponse> {
    return this.delay(this.hr.importPeople(rows, opts));
  }
  async hrRetryNotification(id: string): Promise<void> {
    this.hr.retry(id);
    await this.delay(null);
  }

  closureCost(q: ClosureQuery): Promise<ClosureCost> {
    return this.delay(mockClosureCost(q));
  }

  criticalPositions(_siteId: string, from: string, to: string): Promise<CriticalReport> {
    return this.delay(mockCriticalPositions(from, to));
  }

  driftChecks(): Promise<DriftCheck[]> {
    return this.delay(mockDriftChecks());
  }

  driftSchedule(): Promise<DriftSchedule> {
    return this.delay(mockDriftSchedule());
  }

  populations(): Promise<Population[]> {
    return this.delay(mockPopulations());
  }

  fits(_siteId: string, population: string): Promise<Fit[]> {
    return this.delay(mockFits(population));
  }

  /* ── the migration, as the sample tells it ───────────────────────────── */

  migrationStatus(): Promise<MigrationStatus> {
    return this.delay(mockMigrationStatus());
  }

  migrationScreen(key: string): Promise<ScreenDetail> {
    return this.delay(mockMigrationScreen(key));
  }

  migrationDifferences(key: string, untriagedOnly: boolean): Promise<Difference[]> {
    return this.delay(mockMigrationDifferences(key, untriagedOnly));
  }

  triageObservation(id: number, triage: Triage): Promise<void> {
    mockTriage(id, triage);
    return this.delay(undefined);
  }

  setScreenMode(key: string, mode: ScreenMode, reason: string, force = false): Promise<ModeChange> {
    return this.delay(mockSetMode(key, mode, reason, force));
  }

  /* No phones report to the sample, and inventing some would make the map
     claim a live feed it does not have. The engine keeps simulating and keeps
     saying so, which is the honest answer. */
  /* ── administering the platform, on the sample ───────────────────────
     A small deployment part-way through being set up, so the screen has
     something to show and the checklist has something to say. It is held in
     memory and changes as you work it: invite somebody and they appear. It
     says "sample" wherever it is shown. */

  adminSetup(): Promise<SetupState> {
    const people = this.people;
    const roles = SAMPLE_ROLES;
    const admins = people.filter(
      (p) => !p.disabledAt && p.roles.some((k) => roles.find((r) => r.key === k)?.accessLevel === "master"),
    ).length;
    const unroled = people.filter((p) => !p.disabledAt && !p.roles.length).length;
    const steps = [
      {
        key: "tenant",
        title: "The organization is named",
        done: true,
        blocking: false,
        is: "Sample estate (tibah)",
        next: "Set the tenant's name and slug when it is provisioned.",
      },
      {
        key: "people",
        title: "The people who will use it are listed",
        done: people.length > 1,
        blocking: false,
        is: `${people.filter((p) => !p.disabledAt).length} active person/people`,
        next: "Add everybody who will sign in, with the subject their identity provider will send.",
      },
      {
        key: "roles",
        title: "Everybody holds a role",
        done: unroled === 0,
        blocking: false,
        is: unroled ? `${unroled} hold no role` : "everybody holds at least one",
        next: "A principal with no role holds no permission and sees an empty platform.",
      },
      {
        key: "admins",
        title: "More than one person can administer it",
        done: admins >= 2,
        blocking: admins === 0,
        is: `${admins} person/people hold a master-level role`,
        next: "One administrator is one illness away from nobody being able to grant a role.",
      },
      {
        key: "sign_in",
        title: "Sign-in goes through an identity provider",
        done: false,
        blocking: false,
        is: "none — this is the built-in sample, which has no provider",
        next: "Configure the organisation's OIDC provider on a real deployment.",
      },
      {
        key: "dev_header",
        title: "The development header is off",
        done: true,
        blocking: false,
        is: "off — the sample has no server to trust a header",
        next: "Unset SLT_DEV_AUTH before a deployment carries anything real.",
      },
      {
        key: "levels",
        title: "Roles carry the level their holders are offered",
        done: true,
        blocking: false,
        is: "operations 2, engineering 1, executive 1, master 1",
        next: "Check each role's level. A level decides what is shown, never what is permitted.",
      },
      {
        key: "packs",
        title: "A pack is installed",
        done: true,
        blocking: false,
        is: "1 pack enabled (airports)",
        next: "A pack carries the asset types, checklists and rules for this kind of estate.",
      },
      {
        key: "estate",
        title: "The estate is loaded",
        done: true,
        blocking: false,
        is: "1 site, 1,014 surveyed assets",
        next: "Load the asset register and the drawing through Intake.",
      },
      {
        key: "work",
        title: "Work is flowing through it",
        done: true,
        blocking: false,
        is: "14 work orders on record",
        next: "The platform is only as real as what people put in it.",
      },
      {
        key: "keys",
        title: "Machine access is by issued key",
        done: false,
        blocking: false,
        is: "none — the sample authenticates nobody",
        next: "Issue a key per integration, scoped to what it needs.",
      },
    ];
    const blocking = steps.filter((x) => x.blocking).map((x) => x.key);
    return this.delay({
      steps,
      done: steps.filter((x) => x.done).length,
      total: steps.length,
      blocking,
      ready: !blocking.length,
      note: "This is the built-in sample, not a deployment. On a platform these steps are computed from its own database on every read.",
    });
  }

  adminPeople(): Promise<Person[]> {
    return this.delay(this.people.map((p) => ({ ...p })));
  }

  adminAddPerson(input: NewPerson): Promise<AddedPerson> {
    const known = SAMPLE_ROLES.map((r) => r.key);
    const roles = input.roles.filter((r) => known.includes(r));
    const refused = input.roles.filter((r) => !known.includes(r)).map((role) => ({ role, why: "no role of that key" }));
    const person: Person = {
      id: `sample-${this.people.length + 1}`,
      kind: input.kind ?? "user",
      externalId: input.externalId,
      displayName: input.displayName,
      email: input.email,
      groups: [],
      roles,
    };
    this.people = [...this.people, person];
    const order: AccessLevel[] = ["operations", "engineering", "executive", "master"];
    let best = 0;
    for (const k of roles)
      best = Math.max(best, order.indexOf(SAMPLE_ROLES.find((r) => r.key === k)?.accessLevel ?? "operations"));
    return this.delay({
      id: person.id,
      displayName: person.displayName,
      roles,
      refused,
      accessLevel: order[best],
      note: "No invitation was sent: this platform has no inbox. On a deployment the person signs in through the identity provider with this subject.",
    });
  }

  adminSetDisabled(principalId: string, disabled: boolean): Promise<void> {
    this.people = this.people.map((p) =>
      p.id === principalId ? { ...p, disabledAt: disabled ? new Date().toISOString() : undefined } : p,
    );
    return this.delay(undefined);
  }

  adminRoles(): Promise<RoleRow[]> {
    return this.delay(SAMPLE_ROLES.map((r) => ({ ...r })));
  }

  adminSetRoleLevel(key: string, level: AccessLevel): Promise<void> {
    const r = SAMPLE_ROLES.find((x) => x.key === key);
    if (r && !r.builtin) r.accessLevel = level;
    return this.delay(undefined);
  }

  adminAssignRole(principal: string, role: string): Promise<void> {
    this.people = this.people.map((p) =>
      p.id === principal || p.externalId === principal
        ? { ...p, roles: p.roles.includes(role) ? p.roles : [...p.roles, role] }
        : p,
    );
    return this.delay(undefined);
  }

  adminUnassignRole(principal: string, role: string): Promise<void> {
    this.people = this.people.map((p) =>
      p.id === principal || p.externalId === principal ? { ...p, roles: p.roles.filter((r) => r !== role) } : p,
    );
    return this.delay(undefined);
  }

  /* The sample's one provider, so the sign-in controls have something to act on. */
  private provider = {
    id: "sample-provider",
    name: "Google Identity Platform (sample)",
    issuer: "https://securetoken.google.com/sample",
    enabled: true,
    cachedKeys: 0,
    defaultRole: undefined as string | undefined,
    canInvite: true,
  };

  adminInvite(input: Invitation): Promise<Invited> {
    const existing = this.people.find((p) => p.email === input.email);
    const role = SAMPLE_ROLES.find((r) => r.key === input.role);
    if (!role) return Promise.reject(new DataSourceError(`there is no role '${input.role}'`, { status: 400 }));
    if (existing) {
      this.people = this.people.map((p) =>
        p === existing ? { ...p, roles: p.roles.includes(role.key) ? p.roles : [...p.roles, role.key] } : p,
      );
    } else {
      this.people = [
        ...this.people,
        {
          id: `sample-${this.people.length + 1}`,
          kind: "user",
          externalId: `sample-uid-${this.people.length + 1}`,
          displayName: input.displayName,
          email: input.email,
          groups: [],
          roles: [role.key],
        },
      ];
    }
    /* No email leaves a sample: `emailSent` says so, and the screen does too. */
    return this.delay({
      displayName: input.displayName,
      email: input.email,
      role: role.key,
      accessLevel: role.accessLevel,
      accountCreated: !existing,
      alreadyListed: !!existing,
      emailSent: false,
    });
  }

  adminUpdateProvider(_id: string, change: ProviderChange): Promise<void> {
    if (change.enabled !== undefined) this.provider = { ...this.provider, enabled: change.enabled };
    if (change.defaultRole !== undefined)
      this.provider = { ...this.provider, defaultRole: change.defaultRole ?? undefined };
    return this.delay(undefined);
  }

  adminRefreshProviderKeys(_id: string): Promise<number> {
    return this.delay(0);
  }

  adminAuthStatus(): Promise<AuthStatus> {
    return this.delay({
      method: "sample",
      principal: this.principal,
      providers: [{ ...this.provider }],
      developmentHeaderEnabled: false,
    });
  }

  fieldPositions(): Promise<FieldPosition[]> {
    return this.delay([]);
  }

  ledgerFacts(): Promise<FactSummary[]> {
    return this.delay(mockLedgerFacts());
  }

  divergence(q: DivergenceQuery): Promise<Divergence> {
    return this.delay(mockDivergence(q));
  }

  canWork(_siteId: string, category: string, at: string): Promise<CanWork> {
    return this.delay(mockCanWork(category, at));
  }

  programme(_siteId: string, fiscalYear: number, envelope?: number): Promise<Programme> {
    return this.delay(mockProgramme(fiscalYear, envelope));
  }

  counterfactual(_siteId: string, fiscalYear: number, crews?: number): Promise<Study> {
    return this.delay(mockStudy(fiscalYear, crews));
  }

  provenance(_siteId: string, candidateId: string): Promise<Chain> {
    return this.delay(mockChain(candidateId));
  }

  async createDocument(input: NewDocumentInput): Promise<NewDocumentResult> {
    const type = DOCUMENT_TYPES.find((x) => x.key === input.type);
    if (!type)
      throw new DataSourceError(
        `document type '${input.type}' is not declared by any installed pack; document types are pack content, not something a caller invents`,
        { status: 422 },
      );
    const classification = input.classification ?? type.defaultClassification ?? "internal";
    if (type.defaultClassification === "restricted" && classification !== "restricted")
      throw new DataSourceError(
        `document type '${input.type}' is declared restricted by its pack and cannot be created at a lower classification`,
        { status: 422 },
      );
    const doc: DocumentDetail = {
      id: `doc-${Math.random().toString(36).slice(2, 8)}`,
      title: input.title,
      reference: input.reference,
      type: input.type,
      status: "draft",
      classification,
      siteId: input.siteId,
      tags: input.tags,
      createdAt: new Date().toISOString(),
      revisions: 0,
      ownerId: this.principal,
      revisionHistory: [],
      links: [],
      acknowledgements: [],
    };
    DOCUMENTS.unshift(doc);
    return this.delay({
      id: doc.id,
      title: doc.title,
      reference: doc.reference,
      classification,
      status: "draft",
      controlled: type.controlled,
    });
  }

  async addRevision(documentId: string, input: NewRevisionInput): Promise<NewRevisionResult> {
    const doc = DOCUMENTS.find((x) => x.id === documentId);
    if (!doc) throw new DataSourceError("no such document", { status: 404 });
    if (doc.status === "withdrawn")
      throw new DataSourceError("this document has been withdrawn; a withdrawn document does not take new revisions", {
        status: 422,
      });
    const type = DOCUMENT_TYPES.find((x) => x.key === doc.type);
    if (type?.expires && !input.expiresOn)
      throw new DataSourceError(
        `document type '${doc.type}' declares an expiry, so this revision needs an expires_on date — a certificate with no expiry is a certificate nobody will notice lapsing`,
        { status: 422 },
      );
    const sequence = Math.max(0, ...doc.revisionHistory.map((r) => r.sequence)) + 1;
    const base = input.issuedOn ? new Date(input.issuedOn) : new Date();
    const reviewDueOn = type?.reviewMonths
      ? new Date(base.getTime() + 30.44 * type.reviewMonths * 86_400_000).toISOString().slice(0, 10)
      : undefined;
    const rev: Revision = {
      id: `${doc.id}-r${sequence}`,
      revision: input.revision,
      sequence,
      status: "draft",
      issuedOn: input.issuedOn,
      expiresOn: input.expiresOn,
      reviewDueOn,
      changeNote: input.changeNote,
      createdAt: new Date().toISOString(),
      author: this.principal,
      bytes: input.file?.size,
      contentType: input.file?.type || undefined,
    };
    doc.revisionHistory.unshift(rev);
    doc.revisions = doc.revisionHistory.length;
    return this.delay({
      id: rev.id,
      revision: rev.revision,
      sequence,
      status: "draft",
      reviewDueOn,
      expiresOn: input.expiresOn,
    });
  }

  /* ── build 4: approvals on the stores chain ── */
  /*
   * The demo's own chain (mock/approvals.ts). The Approvals board on the
   * sample keeps its four sample requests and its picker and never calls
   * these; they answer — and refuse — as the platform does, signed by the
   * sample's one person, who holds what the bootstrap above says.
   */
  private get approver() {
    const held = new Set<string>(MOCK_APPROVER_PERMISSIONS);
    return { name: this.principal, holds: (p: string) => held.has(p) };
  }

  async approvalsBoard(siteId: string): Promise<ApprovalsBoard> {
    return this.delay(mockApprovalsBoard(siteId, this.approver));
  }

  async raiseApprovalRequest(siteId: string, input: NewApprovalRequest, key?: string) {
    return this.delay(mockRaiseApprovalRequest(siteId, input, this.approver, key));
  }

  async signApprovalStep(
    siteId: string,
    number: string,
    decision: "approved" | "rejected",
    note?: string,
    key?: string,
  ): Promise<ApprovalStepAck> {
    return this.delay(mockSignApprovalStep(siteId, number, decision, note, this.approver, key));
  }

  async orderApprovalRequest(siteId: string, number: string, supplier?: string, key?: string) {
    return this.delay(mockOrderApprovalRequest(siteId, number, supplier, this.approver, key));
  }

  /* ── build 2: certificates ── */
  /* The demo's own register (mock/certificates.ts), never the demo library: see that file. */

  async recordCertificate(input: NewCertificateInput, key?: string): Promise<CertificateWrite> {
    return this.delay(mockRecordCertificate(input, key));
  }

  async renewCertificate(documentId: string, input: CertificateIssueInput, key?: string): Promise<CertificateWrite> {
    return this.delay(mockRenewCertificate(documentId, input, key));
  }

  async issueCertificate(revisionId: string): Promise<RevisionTransitionResult> {
    return this.delay(mockIssueCertificate(revisionId));
  }

  async attachRevisionFile(revisionId: string, file: File, key?: string): Promise<AttachedFile> {
    return this.delay(mockAttachFile(revisionId, file, key));
  }

  async downloadRevision(revisionId: string): Promise<Blob> {
    const blob = mockDownload(revisionId);
    if (this.latency > 0) await new Promise((r) => setTimeout(r, this.latency));
    return blob;
  }

  async transitionRevision(revisionId: string, input: RevisionTransitionInput): Promise<RevisionTransitionResult> {
    const doc = DOCUMENTS.find((x) => x.revisionHistory.some((r) => r.id === revisionId));
    const rev = doc?.revisionHistory.find((r) => r.id === revisionId);
    if (!doc || !rev) throw new DataSourceError("no such revision", { status: 404 });
    const allowed = REVISION_TRANSITIONS[rev.status];
    if (!allowed.includes(input.to))
      throw new DataSourceError(
        `a revision that is ${rev.status} cannot become ${input.to}; from here it may only become ${
          allowed.length ? JSON.stringify([...allowed].sort()) : "nothing"
        }`,
        { status: 409 },
      );
    const type = DOCUMENT_TYPES.find((x) => x.key === doc.type);
    if (input.to === "issued" && rev.status === "draft" && type?.requiresApproval)
      throw new DataSourceError(
        `document type '${doc.type}' requires approval, so this revision must go through review before it can be issued`,
        { status: 409 },
      );
    if (input.to === "approved" && rev.author === this.principal)
      throw new DataSourceError(
        "the author of a revision cannot approve it (segregation of duties) — an approval you can grant yourself is a checkbox, not a control",
        { status: 409 },
      );
    const from = rev.status;
    const now = new Date().toISOString();
    rev.status = input.to;
    if (input.to === "approved") {
      rev.approvedBy = this.principal;
      rev.approvedAt = now;
    } else if (input.to === "rejected") {
      rev.rejectedReason = input.note ?? undefined;
    } else if (input.to === "issued") {
      rev.issuedOn = rev.issuedOn ?? now.slice(0, 10);
      rev.effectiveFrom = input.effectiveFrom ?? now;
      if (!rev.reviewDueOn && type?.reviewMonths)
        rev.reviewDueOn = new Date(Date.now() + 30.44 * type.reviewMonths * 86_400_000).toISOString().slice(0, 10);
      for (const other of doc.revisionHistory)
        if (other !== rev && other.status === "issued") {
          other.status = "superseded";
          other.supersededAt = rev.effectiveFrom;
        }
      doc.status = "issued";
      doc.revision = rev.revision;
      doc.issuedOn = rev.issuedOn;
      doc.expiresOn = rev.expiresOn;
      doc.reviewDueOn = rev.reviewDueOn;
      doc.revisionStatus = "issued";
      for (const a of doc.acknowledgements) a.isCurrent = a.revision === rev.revision;
    } else if (input.to === "withdrawn" && doc.revision === rev.revision) {
      doc.revision = undefined;
      doc.revisionStatus = undefined;
    }
    if (input.to === "in_review") doc.status = doc.status === "issued" ? "issued" : "in_review";
    for (const a of doc.acknowledgements) if (a.revision === rev.revision) a.revisionStatus = rev.status;
    return this.delay({ revisionId, from, to: input.to });
  }

  async acknowledgeRevision(revisionId: string): Promise<{ acknowledged: boolean }> {
    const doc = DOCUMENTS.find((x) => x.revisionHistory.some((r) => r.id === revisionId));
    const rev = doc?.revisionHistory.find((r) => r.id === revisionId);
    const mine = doc?.acknowledgements.find(
      (a) => rev && a.revision === rev.revision && a.person === this.principal && !a.acknowledgedAt,
    );
    if (mine) mine.acknowledgedAt = new Date().toISOString();
    return this.delay({ acknowledged: Boolean(mine) });
  }

  /* ---- capital projects ---------------------------------------------------------- */

  getProjectVocabulary(): Promise<ProjectVocabulary> {
    return this.delay(PROJECT_VOCABULARY);
  }

  listProjects(q: ProjectQuery): Promise<ProjectRow[]> {
    const rows = projectRows(q.siteId)
      .filter((p) => !q.status || p.status === q.status)
      .filter((p) => !q.category || p.category === q.category)
      .filter((p) => !q.liveOnly || LIVE_STATUSES.includes(p.status));
    return this.delay(rows);
  }

  async createProject(siteId: string, input: CreateProjectInput): Promise<{ id: string; code: string }> {
    if (!PROJECT_VOCABULARY.categories.some((c) => c.key === input.category))
      throw new DataSourceError(`unknown category '${input.category}'`, { status: 400 });
    const code = input.code || `PRJ-${String(PROJECTS.length + 1).padStart(4, "0")}`;
    if (PROJECTS.some((p) => p.code === code)) throw new DataSourceError(`code ${code} is taken`, { status: 400 });
    const hasDates = Boolean(input.startDate || input.endDate || input.baselineStart || input.baselineEnd);
    const project: ProjectDetail = {
      id: crypto.randomUUID(),
      code,
      name: input.name,
      category: input.category,
      status: input.status ?? "planned",
      siteId,
      contractor: input.contractor,
      consultant: input.consultant,
      description: input.description,
      schedule: {
        state: input.baselineEnd ? "on_baseline" : hasDates ? "no_baseline" : "no_dates",
        baselineStart: input.baselineStart,
        baselineEnd: input.baselineEnd,
        startDate: input.startDate,
        endDate: input.endDate,
        rebaselined: 0,
      },
      money:
        input.budgetMinor != null
          ? { state: "declared", currency: input.currency ?? "SAR", budgetMinor: input.budgetMinor }
          : { state: "not_declared" },
      gateSet: { key: input.gateSet ?? PROJECT_VOCABULARY.gateSets[0]?.key ?? "capital_works" },
      gates: 0,
      gatesApproved: 0,
      holding: 0,
      workStopped: false,
      createdAt: new Date().toISOString(),
      gateList: [],
      gateSummary: { count: 0, decided: 0, nextGate: null, decidedWithoutEvidence: [], state: "no_gates" },
      inspections: [],
      links: [],
      holdPoints: [],
      rebaselines: [],
    };
    PROJECTS.push(project);
    return this.delay({ id: project.id, code });
  }

  async updateProject(id: string, input: UpdateProjectInput): Promise<void> {
    const p = PROJECTS.find((x) => x.id === id);
    if (!p) throw new DataSourceError("no such project", { status: 404 });
    Object.assign(p, {
      ...(input.name != null && { name: input.name }),
      ...(input.description != null && { description: input.description }),
      ...(input.status != null && { status: input.status }),
      ...(input.category != null && { category: input.category }),
      ...(input.contractor != null && { contractor: input.contractor }),
      ...(input.consultant != null && { consultant: input.consultant }),
    });
    if (input.budgetMinor != null || input.committedMinor != null || input.spentMinor != null)
      p.money = {
        ...p.money,
        state: "declared",
        currency: p.money.currency ?? "SAR",
        budgetMinor: input.budgetMinor ?? p.money.budgetMinor,
        committedMinor: input.committedMinor ?? p.money.committedMinor,
        spentMinor: input.spentMinor ?? p.money.spentMinor,
      };
    return this.delay(undefined);
  }

  async setProjectSchedule(id: string, input: ScheduleInput): Promise<ScheduleResult> {
    const p = PROJECTS.find((x) => x.id === id);
    if (!p) throw new DataSourceError("no such project", { status: 404 });
    const sc = p.schedule;
    const hadBaseline = Boolean(sc.baselineStart || sc.baselineEnd);
    const moving =
      (input.baselineStart != null && input.baselineStart !== sc.baselineStart) ||
      (input.baselineEnd != null && input.baselineEnd !== sc.baselineEnd);
    if (moving && hadBaseline && !input.rebaselineReason?.trim())
      throw new DataSourceError(`project ${p.code} already has a baseline. Moving it requires a reason`, {
        status: 409,
      });
    if (moving && hadBaseline) {
      p.rebaselines = [
        ...p.rebaselines,
        {
          baselineStart: sc.baselineStart,
          baselineEnd: sc.baselineEnd,
          replacedAt: new Date().toISOString(),
          reason: input.rebaselineReason,
        },
      ];
      sc.rebaselined += 1;
    }
    Object.assign(sc, {
      ...(input.startDate && { startDate: input.startDate }),
      ...(input.endDate && { endDate: input.endDate }),
      ...(input.baselineStart && { baselineStart: input.baselineStart }),
      ...(input.baselineEnd && { baselineEnd: input.baselineEnd }),
    });
    return this.delay({
      rebaselined: moving && hadBaseline,
      warnings:
        moving && !hadBaseline
          ? ["this project was baselined for the first time; from now on a change to these dates needs a reason"]
          : [],
    });
  }

  async getProjectLog(id: string): Promise<ProjectLog> {
    const today = new Date().toISOString().slice(0, 10);
    const entries = [...(this.projectLogs.get(id) ?? [])]
      .reverse()
      .map((e) => ({ ...e, overdue: e.status === "open" && !!e.dueDate && e.dueDate < today }));
    const latest = entries.find((e) => e.kind === "update" && e.progressPct != null);
    return this.delay({
      entries,
      openActions: entries.filter((e) => e.status === "open").length,
      overdueActions: entries.filter((e) => e.overdue).length,
      latestProgress: latest
        ? { pct: latest.progressPct!, reportedAt: latest.createdAt, reportedBy: latest.createdBy }
        : undefined,
    });
  }

  async addProjectLogEntry(id: string, input: NewLogEntryInput): Promise<void> {
    if (!input.body.trim()) throw new DataSourceError("an entry needs some text", { status: 400 });
    if (input.kind === "action" && !input.owner?.trim())
      throw new DataSourceError("an action needs an owner — an action nobody owns is a note", { status: 400 });
    const list = this.projectLogs.get(id) ?? [];
    list.push({
      id: crypto.randomUUID(),
      kind: input.kind,
      body: input.body.trim(),
      progressPct: input.kind === "update" ? input.progressPct : undefined,
      owner: input.kind === "action" ? input.owner?.trim() : undefined,
      dueDate: input.kind === "action" ? input.dueDate : undefined,
      status: input.kind === "action" ? "open" : undefined,
      overdue: false,
      createdAt: new Date().toISOString(),
      createdBy: this.principal,
    });
    this.projectLogs.set(id, list);
    return this.delay(undefined);
  }

  async closeProjectAction(entryId: string, input: CloseActionInput): Promise<void> {
    const entry = [...this.projectLogs.values()].flat().find((e) => e.id === entryId);
    if (!entry) throw new DataSourceError("no such entry", { status: 404 });
    if (entry.kind !== "action") throw new DataSourceError("only an action can be closed", { status: 409 });
    if (entry.status !== "open") throw new DataSourceError(`this action is already ${entry.status}`, { status: 409 });
    if (input.outcome === "cancelled" && !input.note?.trim())
      throw new DataSourceError("cancelling an action needs a note saying why", { status: 409 });
    Object.assign(entry, {
      status: input.outcome,
      closedAt: new Date().toISOString(),
      closedBy: this.principal,
      closureNote: input.note,
    });
    return this.delay(undefined);
  }

  async getProject(id: string): Promise<ProjectDetail> {
    const p = PROJECTS.find((x) => x.id === id || x.code === id);
    if (!p) throw new DataSourceError("no such project", { status: 404 });
    return this.delay(p);
  }

  listHoldPoints(siteId: string): Promise<HoldPointReport> {
    return this.delay(holdPointReport(siteId));
  }

  getProjectAttention(siteId: string): Promise<AttentionReport> {
    return this.delay(attentionReport(siteId));
  }

  async getProjectInspection(id: string): Promise<ProjectInspectionDetail> {
    const s = this.sheet(id);
    return this.delay({
      ...s,
      stoppingWork: s.kind === "hold" && !s.releasedAt,
      holdingBecause:
        s.kind === "hold" && !s.releasedAt
          ? holdingBecause(s.result, s.quality.notChecked, s.quality.items)
          : undefined,
    });
  }

  async submitGate(projectId: string, gateKey: string, input: GateSubmitInput): Promise<GateSubmitResult> {
    const { project, gate } = this.gate(projectId, gateKey);
    if (GATE_DECIDED.includes(gate.status))
      throw new DataSourceError(
        `gate '${gateKey}' on ${project.code} is already ${gate.status}; a decided gate is re-opened by deciding it again, not by resubmitting it`,
        { status: 409 },
      );
    /* Evidence accumulates rather than replacing: losing the first set would
       make the record of an earlier rejection unreadable. */
    const supplied = [...new Set([...gate.evidence.supplied, ...(input.evidence ?? [])])];
    gate.status = "submitted";
    gate.submittedAt = new Date().toISOString();
    gate.dueDate = input.dueDate ?? gate.dueDate;
    gate.evidence = evidenceOf("submitted", gate.evidence.required, supplied);
    refresh(project);
    return this.delay({
      key: gate.key,
      status: gate.status,
      submittedAt: gate.submittedAt,
      evidence: gate.evidence,
      warnings: gate.evidence.missing.length
        ? [
            `submitted with ${gate.evidence.missing.length} required item(s) missing: ${gate.evidence.missing.join(", ")}`,
          ]
        : [],
    });
  }

  /**
   * Deciding a gate. The mock refuses a waiver with no reason, as the platform
   * does, and — as the platform does — does NOT refuse a decision taken with
   * the evidence unmet. It records it and warns, because refusing produces
   * gates decided in a meeting and recorded nowhere.
   */
  async decideGate(projectId: string, gateKey: string, input: GateDecisionInput): Promise<GateDecisionResult> {
    const { project, gate } = this.gate(projectId, gateKey);
    if (input.decision === "waived" && !input.note?.trim())
      throw new DataSourceError(
        `waiving gate '${gateKey}' requires a note saying why. A waiver with no reason reads afterwards as a gate that never applied`,
        { status: 409 },
      );
    const supplied = [...new Set([...gate.evidence.supplied, ...(input.evidence ?? [])])];
    gate.status = input.decision;
    gate.decidedAt = new Date().toISOString();
    gate.decidedByName = this.principal;
    gate.decisionNote = input.note ?? gate.decisionNote;
    gate.evidence = evidenceOf(input.decision, gate.evidence.required, supplied);

    /* Earlier gates nobody has decided. Reported, never refused — a programme
       that decides a later gate first is doing something deliberate, and a
       refusal here would move that decision somewhere with no record at all. */
    const outOfOrder = project.gateList
      .filter((g) => g.seq < gate.seq && !GATE_DECIDED.includes(g.status))
      .map((g) => g.key);

    const warnings: string[] = [];
    if (gate.evidence.state === "decided_without_evidence")
      warnings.push(
        `gate '${gateKey}' was ${input.decision} with ${gate.evidence.missing.length} of ${gate.evidence.required.length} required item(s) never supplied: ${gate.evidence.missing.join(
          ", ",
        )}. This is recorded on the gate, on the event and in the audit log, and stays visible`,
      );
    if (outOfOrder.length)
      warnings.push(`${outOfOrder.length} earlier gate(s) are still undecided: ${outOfOrder.join(", ")}`);
    refresh(project);
    return this.delay({
      key: gate.key,
      decision: input.decision,
      decidedAt: gate.decidedAt,
      projectCode: project.code,
      evidence: gate.evidence,
      outOfOrderAfter: outOfOrder,
      warnings,
    });
  }

  async recordInspectionItems(inspectionId: string, results: ItemResultInput[]): Promise<InspectionWriteResult> {
    const s = this.sheet(inspectionId);
    if (s.releasedAt)
      throw new DataSourceError(
        `${s.reference} was released at ${s.releasedAt}; changing its answers now would rewrite the sheet the release was given against`,
        { status: 400 },
      );
    let recorded = 0;
    const unknown: (number | string)[] = [];
    for (const r of results) {
      const item = s.sheet.find((i) => (r.seq != null ? i.seq === r.seq : i.itemKey === r.itemKey));
      if (!item) {
        unknown.push(r.seq ?? r.itemKey ?? "?");
        continue;
      }
      item.result = r.result;
      item.valueNum = r.valueNum ?? item.valueNum;
      item.valueText = r.valueText ?? item.valueText;
      item.note = r.note ?? item.note;
      recorded += 1;
    }
    s.quality = qualityOf(s.sheet);
    this.refreshOwner(s);
    if (unknown.length)
      throw new DataSourceError(
        `${unknown.length} answer(s) matched no item on ${s.reference}: ${JSON.stringify(unknown)}. Nothing was recorded for them, and the items they were meant for are still unchecked`,
        { status: 400 },
      );
    return this.delay({ reference: s.reference, recorded, quality: s.quality, warnings: [] });
  }

  /**
   * Record what was found. This does not release a hold point, and the result
   * says so in the platform's own words when the hold is still holding.
   */
  async recordInspectionResult(inspectionId: string, input: RecordResultInput): Promise<InspectionWriteResult> {
    const s = this.sheet(inspectionId);
    if (s.releasedAt)
      throw new DataSourceError(`${s.reference} was released at ${s.releasedAt}; its result cannot be changed now`, {
        status: 409,
      });
    const unchecked = s.sheet.filter((i) => i.result === "not_checked");
    if (ASSERTS_ACCEPTABLE.includes(input.result) && unchecked.length)
      throw new DataSourceError(
        `${s.reference} cannot be recorded as '${input.result}' while ${unchecked.length} item(s) are unchecked: ${unchecked
          .slice(0, 8)
          .map((i) => i.seq)
          .join(", ")}. Check them or mark them not applicable`,
        { status: 409 },
      );
    s.result = input.result;
    s.notes = input.notes ?? s.notes;
    s.witnessedBy = input.witnessedBy ?? s.witnessedBy;
    s.performedAt = input.performedAt ?? new Date().toISOString();
    s.performedByName = this.principal;
    s.quality = qualityOf(s.sheet);
    this.refreshOwner(s);

    const warnings: string[] = [];
    if (unchecked.length)
      warnings.push(
        `recorded as '${input.result}' with ${unchecked.length} item(s) still unchecked; this sheet is not complete and is not reported as complete anywhere`,
      );
    if (s.quality.failed && ASSERTS_ACCEPTABLE.includes(input.result))
      warnings.push(
        `${s.quality.failed} item(s) failed but the overall result is '${input.result}'; the failed items stay on the sheet and can raise defects`,
      );
    const stopping = s.kind === "hold" && !s.releasedAt;
    return this.delay({
      reference: s.reference,
      result: s.result,
      performedAt: s.performedAt,
      quality: s.quality,
      stillStoppingWork: stopping,
      why: stopping
        ? "this is a hold point and it is still holding: a result is not a release, and the work does not restart until somebody with the authority to release it does so"
        : undefined,
      warnings,
    });
  }

  /** The three refusals from `inspections.release`, kept verbatim: they are the point of a hold point. */
  async releaseHoldPoint(inspectionId: string, note?: string): Promise<InspectionWriteResult> {
    const s = this.sheet(inspectionId);
    if (s.kind !== "hold")
      throw new DataSourceError(
        `${s.reference} is a '${s.kind}' inspection, which does not stop the work and therefore has nothing to release`,
        { status: 409 },
      );
    if (s.releasedAt)
      throw new DataSourceError(`${s.reference} was already released at ${s.releasedAt}`, { status: 409 });
    if (!s.result)
      throw new DataSourceError(
        `${s.reference} has no result recorded, so nothing has been inspected. Releasing it now would be a signature against a check that never happened`,
        { status: 409 },
      );
    if (BLOCKS_RELEASE.includes(s.result))
      throw new DataSourceError(
        `${s.reference} failed. A failed hold point is cleared by re-inspecting the work, not by releasing it — raise a re_inspection and release that`,
        { status: 409 },
      );
    if (s.quality.notChecked)
      throw new DataSourceError(
        `${s.reference} has ${s.quality.notChecked} of ${s.quality.items} item(s) unchecked. Releasing the work past an unfinished sheet is exactly what the hold point was placed to prevent`,
        { status: 409 },
      );

    s.releasedAt = new Date().toISOString();
    s.releasedByName = this.principal;
    s.notes = note ?? s.notes;
    this.refreshOwner(s);

    const warnings: string[] = [];
    if (s.result === "conditional")
      warnings.push(
        "this hold point was released on a conditional result; the conditions are in the notes and nothing in this platform tracks them to completion. Create a new work order or a re-inspection for anything that has to be done",
      );
    if (s.quality.failed)
      warnings.push(
        `${s.quality.failed} item(s) on this sheet failed and the hold was still released; those items stay on the record`,
      );
    return this.delay({
      reference: s.reference,
      result: s.result,
      releasedAt: s.releasedAt,
      quality: s.quality,
      warnings,
      note: "the work may continue past this point",
    });
  }

  private sheet(id: string): ProjectInspectionDetail {
    const s = SHEETS.find((x) => x.id === id || x.reference === id);
    if (!s) throw new DataSourceError("no such inspection", { status: 404 });
    return s;
  }

  private refreshOwner(s: ProjectInspectionDetail): void {
    const p = PROJECTS.find((x) => x.id === s.projectId);
    if (p) refresh(p);
  }

  private gate(projectId: string, gateKey: string) {
    const project = PROJECTS.find((x) => x.id === projectId || x.code === projectId);
    if (!project) throw new DataSourceError("no such project", { status: 404 });
    const gate = project.gateList.find((g) => g.key === gateKey);
    if (!gate) throw new DataSourceError(`this project has no gate '${gateKey}'`, { status: 409 });
    return { project, gate };
  }

  listNotifications(siteId: string): Promise<Notification[]> {
    if (siteId !== "site_oema") return this.delay([]);
    return this.delay([
      {
        id: "n1",
        at: new Date().toISOString(),
        severity: 5,
        role: "duty_manager",
        title: "Minimum friction rule fired — RWY 17/35 CH 1200–1500",
        ruleKey: "friction_below_minimum",
        subjectKind: "alert",
        subjectId: "al-friction-17",
        read: false,
      },
      {
        id: "n2",
        at: new Date(Date.now() - 3.6e6).toISOString(),
        severity: 2,
        role: "maintenance",
        title: "WO-000012 started by Ops response",
        subjectKind: "work_order",
        read: false,
      },
      {
        id: "n3",
        at: new Date(Date.now() - 2 * 3.6e6).toISOString(),
        severity: 1,
        role: "compliance",
        title: "Daily runway inspection obligation settled for 2026-09-06",
        read: true,
      },
    ]);
  }

  private find(id: string): WorkOrder {
    const wo = this.orders.find((w) => w.id === id || w.number === id);
    if (!wo) throw new DataSourceError("work order not found", { status: 404 });
    return wo;
  }

  private withTransitions(wo: WorkOrder): WorkOrder {
    return { ...wo, allowedTransitions: [...legalTransitions(wo.status)], delivery: this.deliveryCounts(wo.id) };
  }

  /** The dispatch messages for one order counted by status, as the platform's list serves them. */
  private deliveryCounts(id: string): Record<string, number> | undefined {
    const rows = [...(this.dispatchOutbox.get(id) ?? []), ...(this.ruleMessages.get(id) ?? [])].filter(
      (m) => (m.event ?? "dispatched") === "dispatched",
    );
    if (!rows.length) return undefined;
    const out: Record<string, number> = {};
    for (const m of rows) out[m.status] = (out[m.status] ?? 0) + 1;
    return out;
  }
  /**
   * The demo has no platform behind it, so it refuses — and says which.
   *
   * Refusing is the honest answer and not a degraded one. `askAssistant` is
   * the route by which Sky is allowed to state a figure about a real estate,
   * and a mock that invented plausible defect counts to keep the demo lively
   * would be doing the exact thing the whole subsystem is built to prevent.
   * Sky reads `refused` and falls back to what she can read from the screen
   * in front of her, which is all this build actually knows.
   */
  /**
   * No deployment, so no model of its own — stated, not failed.
   *
   * This is what sends the console to a key of its own when somebody has set
   * one, and to Sky's own reading of the screen when nobody has. Throwing
   * here would make the sample build look broken; pretending to be
   * configured would make every composed answer fail at the call.
   */
  skyModel(): Promise<SkyModel> {
    return this.delay({
      configured: false,
      model: null,
      provider: null,
      note: "This build has no platform behind it, so it holds no model key.",
    });
  }

  sendVoiceLog(_lines: string[]): Promise<void> {
    return Promise.resolve();
  }

  /* ── build 5: shared registers, row by row (the platform's conflict rule; mock/registerRows.ts) ── */
  private readonly registers = new MockRegisters(() => this.principal);

  async listRegisterRows(
    siteId: string,
    key: import("../domain/registerRows").RegisterKey,
    opts: { includeDeleted?: boolean } = {},
  ): Promise<import("../domain/registerRows").RegisterRows> {
    return this.delay(this.registers.list(siteId, key, opts.includeDeleted));
  }

  async changeRegisterRows(
    siteId: string,
    key: import("../domain/registerRows").RegisterKey,
    ops: import("../domain/registerRows").RowOp[],
  ): Promise<import("../domain/registerRows").RegisterRow[]> {
    return this.delay(this.registers.change(siteId, key, ops));
  }

  async importRegisterRows(
    siteId: string,
    key: import("../domain/registerRows").RegisterKey,
    rows: { id: string; data: Record<string, unknown> }[],
    _noteId?: string,
  ): Promise<{ imported: number }> {
    return this.delay(this.registers.import(siteId, key, rows));
  }

  converseWithSky(_input: SkyConverseInput): Promise<SkyConverseReply> {
    return Promise.reject(
      new DataSourceError("This build has no platform behind it, so there is nobody to talk to.", {
        status: 501,
      }),
    );
  }

  composeWithModel(): Promise<ComposedText> {
    return Promise.reject(
      new DataSourceError("This build has no platform behind it, so there is nothing to compose with.", {
        status: 501,
      }),
    );
  }

  askAssistant(q: AssistQuestion): Promise<AssistReply> {
    return this.delay({
      question: q.question,
      answer: {
        text: "This build has no platform behind it, so I cannot look anything up.",
        empty: false,
        highlights: [],
        narrated: false,
      },
      parameters: {},
      resolver: "none",
      confidence: 0,
      citations: [],
      refused: true,
      refusalReason: "no platform is connected to this build",
      canAnswer: [],
    });
  }
}

/** Area, trade and contractor as the platform keeps them in attrs: only the ones given, trimmed. */
function classAttrs(input: ClassifyInput): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of ["area", "trade", "contractor"] as const) {
    const v = input[k]?.trim();
    if (v) out[k] = v;
  }
  return out;
}
